'use client';

import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { FolderIcon, UsersIcon, BriefcaseMetalIcon, UsersThreeIcon, UserIcon, UserCircleIcon, PencilIcon, TrashIcon, EyeIcon, EyeSlashIcon, PlusIcon, ArrowsClockwiseIcon, MagnifyingGlassIcon, ProhibitIcon, CheckCircleIcon, ShieldCheckIcon, KeyIcon, WarningOctagonIcon, ArrowLeftIcon, BuildingsIcon } from '@phosphor-icons/react';
import { Card, CardHeader, CardBody } from '@heroui/card';
import { Button } from '@heroui/button';
import { Tabs, Tab, Divider } from "@heroui/react";
import { Table, TableHeader, TableColumn, TableBody, TableRow, TableCell } from '@heroui/table';
import { Spinner } from '@heroui/spinner';
import { Input } from '@heroui/input';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, useDisclosure } from '@heroui/modal';
import Select from "react-select";
import { MediaSelectStyles, TzSelectStylesFullWidth } from '@/shared/styleDefs';
import { Textarea } from '@heroui/input';
import { Checkbox } from '@heroui/checkbox';
import { Tooltip } from '@heroui/tooltip';
import { UserDTO } from '@/shared/types';
import { FeaturesContext } from '@/app/contexts';
import { CSMContext } from '@/contexts/CSMContext';
import { OrganizationsTab } from '@/components/admin/OrganizationsTab';
import { TenantsTab } from '@/components/admin/TenantsTab';
interface CaseDTO {
  id: string;
  name: string;
  case_number?: string;
  description?: string;
  status: string;
  created_at: string;
  updated_at: string;
  user_count?: number;
  file_count?: number;
  case_role?: string;
  owner_id?: string;
  owner?: {
    id: string;
    email: string;
    name: string;
    status: string;
    is_admin: boolean;
  };
  user_roles?: Array<{
    user_id: string;
    case_id: string;
    case_role: string;
    is_active: boolean;
  }>;
  group_assignments?: Array<{
    id: string;
    group_id: string;
    case_id: string;
    case_role: string;
    is_active: boolean;
  }>;
}
interface Group {
  id: string;
  name: string;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
  member_count?: number;
  users?: Array<{
    id: string;
    name: string;
    email: string;
    status: string;
    is_admin: boolean;
    groups?: Array<{
      id: string;
      user_id: string;
      group_id: string;
      joined_at: string;
      is_active: boolean;
      group: {
        id: string;
        name: string;
        description: string;
        is_active: boolean;
      };
    }>;
  }>;
  cases?: Array<{
    id: string;
    name: string;
    status: string;
    case_number: string;
    description: string;
    s3_case_path?: string | null;
    created_by: string;
    created_at: string;
    updated_at: string;
    updated_by: string;
    case_role?: string;
    assigned_at?: string;
  }>;
}
interface GroupMember {
  id: string;
  user_id: string;
  group_id: string;
  joined_at: string;
  is_active: boolean;
  user: {
    id: string;
    email: string;
    name: string;
    status: string;
  };
}
interface GroupCase {
  id: string;
  case_id: string;
  group_id: string;
  case_role: string;
  assigned_at: string;
  is_active: boolean;
  case: {
    id: string;
    name: string;
    status: string;
    case_number: string;
    description: string;
    case_role?: string;
  };
}
interface CaseUserRole {
  user_id: string;
  case_id: string;
  case_role: string;
  is_active: boolean;
  granted_at: string;
  user_name?: string;
  user_email?: string;
  access_type: string; // Added for removal logic: 'case_role_access', 'group_access', 'owner_access'
  group_id?: string; // Added for group access removals
  assignment_id?: string;
}
interface AdminPanelProps {
  /** When provided (e.g. when embedded in main), called instead of router.push to switch back to dashboard view */
  onBackToDashboard?: () => void;
}
interface UserFormData {
  name: string;
  email: string;
  password?: string;
  confirmPassword?: string;
  is_admin: boolean;
  notes?: string;
  status?: string;
}
interface CaseFormData {
  name: string;
  case_number?: string;
  description?: string;
  owner_id?: string;
}
interface GroupFormData {
  name: string;
  description?: string;
}
interface ValidationErrors {
  name?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  description?: string;
  case_number?: string;
  [key: string]: string | undefined;
}
const ROLE_OPTIONS = [{
  key: 'member',
  label: 'Member'
}, {
  key: 'case_owner',
  label: 'Owner'
}, {
  key: 'viewer',
  label: 'Viewer'
}];
const getRoleName = (role: string) => {
  switch (role.toLowerCase()) {
    case 'case_owner':
      return 'Owner';
    case 'member':
      return 'Member';
    case 'viewer':
      return 'Viewer';
    default:
      return 'Member';
  }
};

// Reusable Loading Card Component
const LoadingCard = ({
  message,
  variant = "card"
}: {
  message: string;
  variant?: "card" | "inline";
}) => {
  if (variant === "inline") {
    return <div className="flex justify-center items-center py-12">
        <Spinner size="lg" color="secondary" />
        <p className="text-gray-300 ml-4">{message}</p>
      </div>;
  }
  return <Card classNames={{
    base: "max-w-full bg-white/5 border-white/10",
    body: "text-center flex flex-col items-center justify-center gap-2 mt-6"
  }} data-sentry-element="Card" data-sentry-component="LoadingCard" data-sentry-source-file="AdminPanel.tsx">
      <CardBody data-sentry-element="CardBody" data-sentry-source-file="AdminPanel.tsx">
        <Spinner size="lg" className="text-white" color="secondary" variant="gradient" data-sentry-element="Spinner" data-sentry-source-file="AdminPanel.tsx" />
        <p className="text-gray-300 text-lg font-medium">{message}</p>
      </CardBody>
    </Card>;
};
export const AdminPanel: React.FC<AdminPanelProps> = ({
  onBackToDashboard
}) => {
  const {
    featureFlags
  } = useContext(FeaturesContext) || {};
  const csmContext = useContext(CSMContext);
  const enableCaseManagement = featureFlags?.['enableCaseManagementInUserAdminPanel'] === true;
  const isSuperAdmin = csmContext?.isSuperAdmin ?? false;
  const canManageOrgs = csmContext?.canManageOrgs ?? false;
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [operationalError, setOperationalError] = useState<string | null>(null);
  const [users, setUsers] = useState<UserDTO[]>([]);
  const activeUsers = useMemo(() => users.filter(u => u.status === 'active'), [users]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [cases, setCases] = useState<CaseDTO[]>([]);
  const [casesLoading, setCasesLoading] = useState(false);
  const [casesError, setCasesError] = useState<string | null>(null);
  const [groups, setGroups] = useState<Group[]>([]);
  const [filteredGroups, setFilteredGroups] = useState<Group[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsError, setGroupsError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTab, setSelectedTab] = useState<string>('users');
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [user, setUser] = useState<UserDTO | null>(null);

  // Modal states
  const {
    isOpen: isUserModalOpen,
    onOpen: onUserModalOpen,
    onOpenChange: onUserModalOpenChange
  } = useDisclosure();
  const {
    isOpen: isCaseModalOpen,
    onOpen: onCaseModalOpen,
    onOpenChange: onCaseModalOpenChange
  } = useDisclosure();
  const {
    isOpen: isGroupMemberOpen,
    onOpen: onGroupMemberModalOpen,
    onOpenChange: onGroupMemberModalOpenChange
  } = useDisclosure();
  const {
    isOpen: isGroupCaseOpen,
    onOpen: onGroupCaseModalOpen,
    onOpenChange: onGroupCaseModalOpenChange
  } = useDisclosure();
  const {
    isOpen: isGroupModalOpen,
    onOpen: onGroupModalOpen,
    onClose: onGroupModalClose,
    onOpenChange: onGroupModalOpenChange
  } = useDisclosure();
  const router = useRouter();
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

  // Form states
  const [userFormData, setUserFormData] = useState<UserFormData>({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    is_admin: false,
    status: 'active',
    notes: ''
  });
  const [caseFormData, setCaseFormData] = useState<CaseFormData>({
    name: '',
    description: '',
    case_number: '',
    owner_id: ''
  });
  const [groupForm, setGroupForm] = useState({
    name: '',
    description: '',
    is_active: true
  });
  const [editingItem, setEditingItem] = useState<any>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [viewingItem, setViewingItem] = useState<any>(null);
  const [isViewMode, setIsViewMode] = useState(true);
  const [isCaseViewMode, setIsCaseViewMode] = useState(true);
  const [isGroupViewMode, setIsGroupViewMode] = useState(true);

  // Group members modal state
  const [groupMembers, setGroupMembers] = useState<GroupMember[]>([]);
  const [groupMembersLoading, setGroupMembersLoading] = useState(false);
  const [groupMembersError, setGroupMembersError] = useState<string | null>(null);
  const [availableUsers, setAvailableUsers] = useState<UserDTO[]>([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [assigningUser, setAssigningUser] = useState(false);
  const [changingStatusId, setChangingStatusId] = useState<string | null>(null);
  const [changingAccessId, setChangingAccessId] = useState<string | null>(null);
  const [resettingPasswordId, setResettingPasswordId] = useState<string | null>(null);

  // Group cases modal state
  const [groupCases, setGroupCases] = useState<GroupCase[]>([]);
  const [groupCasesLoading, setGroupCasesLoading] = useState(false);
  const [groupCasesError, setGroupCasesError] = useState<string | null>(null);
  const [availableCases, setAvailableCases] = useState<CaseDTO[]>([]);
  const [selectedCaseId, setSelectedCaseId] = useState('');
  const [selectedRole, setSelectedRole] = useState('member');
  const [assigningCase, setAssigningCase] = useState(false);
  const [changingCaseStatusId, setChangingCaseStatusId] = useState<string | null>(null);
  const {
    isOpen: isAddCaseUserModalOpen,
    onOpen: onAddCaseUserModalOpen,
    onOpenChange: onAddCaseUserModalOpenChange
  } = useDisclosure();
  const {
    isOpen: isTenantAdminModalOpen,
    onOpen: onTenantAdminModalOpen,
    onOpenChange: onTenantAdminModalOpenChange
  } = useDisclosure();
  const [caseUsers, setCaseUsers] = useState<CaseUserRole[]>([]);
  const [caseUsersLoading, setCaseUsersLoading] = useState(false);
  const [caseUsersError, setCaseUsersError] = useState<string | null>(null);
  const [availableCaseUsers, setAvailableCaseUsers] = useState<UserDTO[]>([]);
  const [selectedCaseUserId, setSelectedCaseUserId] = useState('');
  const [selectedCaseUserRole, setSelectedCaseUserRole] = useState('member');
  const [selectedUserForAdmin, setSelectedUserForAdmin] = useState('');
  const [assigningCaseUser, setAssigningCaseUser] = useState(false);
  const [assigningAdmin, setAssigningAdmin] = useState(false);
  const [tenantAdminError, setTenantAdminError] = useState<string | null>(null);
  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        setLoading(true);
        const response = await fetch('/api/protected/user', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        if (!response.ok) {
          throw new Error(`Failed to fetch user data: ${response.statusText}`);
        }
        const data = await response.json();
        const user = data.response;
        if (!user.is_admin) {
          setError('Access denied: Admin privileges required');
          setIsAdmin(false);
        } else {
          setIsAdmin(true);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    };
    checkAdminStatus();
  }, []);
  const fetchUsers = async () => {
    try {
      setUsersLoading(true);
      setUsersError(null);
      const usersResponse = await fetch('/api/protected/users', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      if (!usersResponse.ok) {
        throw new Error(`Failed to fetch users: ${usersResponse.statusText}`);
      }
      const usersData = await usersResponse.json();
      const userList = usersData.response || [];
      setUsers(userList);
    } catch (err) {
      setUsersError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setUsersLoading(false);
    }
  };

  /** Data-only fetch: returns cases or throws. Does not manage loading/error state. */
  const fetchCasesData = async (): Promise<CaseDTO[]> => {
    const response = await fetch('/api/protected/cases', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch cases: ${response.statusText}`);
    }
    const data = await response.json();
    return data.response || [];
  };
  const fetchCases = async (opts?: {
    rethrow?: boolean;
  }) => {
    const rethrow = opts?.rethrow ?? false;
    try {
      setCasesLoading(true);
      setCasesError(null);
      const cases = await fetchCasesData();
      setCases(cases);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setCasesError(message);
      if (rethrow) throw err;
    } finally {
      setCasesLoading(false);
    }
  };
  const fetchGroups = async () => {
    try {
      setGroupsLoading(true);
      setGroupsError(null);
      const groupsResponse = await fetch('/api/protected/groups', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      if (!groupsResponse.ok) {
        throw new Error(`Failed to fetch groups: ${groupsResponse.statusText}`);
      }
      const groupsData = await groupsResponse.json();
      setGroups(groupsData.response || []);
    } catch (err) {
      setGroupsError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setGroupsLoading(false);
    }
  };
  useEffect(() => {
    if (isAdmin && selectedTab === 'users') {
      fetchUsers();
    } else if (isAdmin && enableCaseManagement && selectedTab === 'cases') {
      fetchCases();
    } else if (isAdmin && selectedTab === 'groups') {
      fetchGroups();
      if (enableCaseManagement) {
        fetchCases();
      }
      fetchUsers();
    }
  }, [isAdmin, selectedTab, enableCaseManagement]);
  useEffect(() => {
    // Filter groups based on search term
    if (searchTerm.trim() === '') {
      setFilteredGroups(groups);
    } else {
      const filtered = groups.filter(group => group.name.toLowerCase().includes(searchTerm.toLowerCase()) || group.description && group.description.toLowerCase().includes(searchTerm.toLowerCase()));
      setFilteredGroups(filtered);
    }
  }, [searchTerm, groups]);

  // const handleBackToApp = () => {
  //   router.push('/');
  // };

  const handleViewUser = (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      setViewingItem(user);
      setIsViewMode(true);
      onUserModalOpen();
    }
  };
  const handleUserInputChange = (field: keyof UserFormData, value: string | boolean) => {
    setUserFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };
      return newData;
    });
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };
  const handleCreateUser = () => {
    setViewingItem(null);
    setUserFormData({
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      is_admin: false,
      status: 'active',
      notes: ''
    });
    setEditingItem(null);
    setIsEditing(false);
    setIsViewMode(false);
    setValidationErrors({});
    onUserModalOpen();
  };
  const resetUserModalState = () => {
    setViewingItem(null);
    setUserFormData({
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      is_admin: false,
      status: 'active',
      notes: ''
    });
    setEditingItem(null);
    setIsEditing(false);
    setIsViewMode(true);
    setShowPassword(false);
    setShowConfirmPassword(false);
    setValidationErrors({});
  };
  const resetCaseModalState = () => {
    setViewingItem(null);
    setCaseFormData({
      name: '',
      description: '',
      owner_id: ''
    });
    setEditingItem(null);
    setIsEditing(false);
    setIsCaseViewMode(true);
    setValidationErrors({});
  };
  const resetGroupModalState = () => {
    setViewingItem(null);
    setIsGroupViewMode(true);
    setIsEditing(false);
    setEditingItem(null);
    setGroupForm({
      name: '',
      description: '',
      is_active: true
    });
    setValidationErrors({});
  };
  const handleEditUser = (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      setUserFormData({
        name: user.name,
        email: user.email,
        status: user.status,
        is_admin: user.is_admin
      });
      setViewingItem(user);
      setEditingItem(user);
      setIsEditing(true);
      setIsViewMode(false);
      onUserModalOpen();
    }
  };
  const handleViewCase = (caseId: string) => {
    const caseItem = cases.find(c => c.id === caseId);
    if (caseItem) {
      setViewingItem(caseItem);
      setIsCaseViewMode(true);
      setIsEditing(false);
      fetchCaseUsers(caseId);
      onCaseModalOpen();
    }
  };
  //
  // const handleEditCase = (caseId: string) => {
  //   const caseItem = cases.find(c => c.id === caseId);
  //   if (caseItem) {
  //     setCaseForm({
  //       name: caseItem.name,
  //       description: caseItem.description || '',
  //       status: caseItem.status
  //     });
  //     setViewingItem(caseItem);
  //     setEditingItem(caseItem);
  //     setIsEditing(true);
  //     setIsCaseViewMode(false);
  //     onCaseModalOpen();
  //   }
  // };

  const handleUpdateCaseStatus = async (caseItem: CaseDTO) => {
    const isActive = caseItem.status === 'active';
    const action = isActive ? 'deactivate' : 'reactivate';
    const confirmMessage = isActive ? 'Are you sure you want to deactivate this case? Members will lose access.' : 'Are you sure you want to reactivate this case?';
    if (!confirm(confirmMessage)) {
      return;
    }
    setChangingStatusId(caseItem.id);
    setError(null);
    try {
      const method = isActive ? 'DELETE' : 'PUT';
      const response = await fetch(`/api/protected/cases/${caseItem.id}/status`, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to ${action} case: ${response.statusText}`);
      }
      await fetchCases();
      const updatedCaseResponse = await fetch(`/api/protected/cases/${caseItem.id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      if (updatedCaseResponse.ok) {
        const updatedCaseData = await updatedCaseResponse.json();
        setViewingItem(updatedCaseData.response);
      }
    } catch (err) {
      setOperationalError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setChangingStatusId(null);
    }
  };
  const handleSyncCases = async () => {
    setCasesError(null);
    try {
      setCasesLoading(true);
      const response = await fetch('/api/protected/cases', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to sync cases');
      }
      const cases = await fetchCasesData();
      setCases(cases);
    } catch (err) {
      setCasesError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setCasesLoading(false);
    }
  };
  const handleCreateGroup = () => {
    setGroupForm({
      name: '',
      description: '',
      is_active: true
    });
    setEditingItem(null);
    setIsEditing(false);
    setIsGroupViewMode(false);
    onGroupModalOpen();
  };
  const handleViewGroup = (groupId: string) => {
    const group = groups.find(g => g.id === groupId);
    if (group) {
      setViewingItem(group);
      setIsGroupViewMode(true);
      setIsEditing(false);

      // Use data already available in the group object
      if (group.users) {
        const membersData = group.users.map(user => ({
          id: user.groups?.[0]?.id || user.id,
          user_id: user.id,
          group_id: group.id,
          joined_at: user.groups?.[0]?.joined_at || new Date().toISOString(),
          is_active: user.groups?.[0]?.is_active ?? true,
          user: {
            id: user.id,
            email: user.email,
            name: user.name,
            status: user.status
          }
        }));
        setGroupMembers(membersData);
      } else {
        setGroupMembers([]);
      }
      if (group.cases) {
        const casesData = group.cases.map(caseItem => ({
          id: caseItem.id,
          case_id: caseItem.id,
          group_id: group.id,
          case_role: caseItem.case_role || '',
          assigned_at: caseItem.assigned_at || caseItem.created_at || new Date().toISOString(),
          is_active: caseItem.status === 'active',
          case: {
            id: caseItem.id,
            name: caseItem.name,
            status: caseItem.status,
            case_number: caseItem.case_number,
            description: caseItem.description || '',
            case_role: caseItem.case_role || ''
          }
        }));
        setGroupCases(casesData);
      } else {
        setGroupCases([]);
      }

      // Calculate available users and cases for assignment (only active users)
      const assignedUserIds = new Set(group.users?.map(u => u.id) || []);
      const available = users.filter(user => user.status === 'active' && !assignedUserIds.has(user.id));
      setAvailableUsers(available);
      const assignedCaseIds = new Set(group.cases?.map(c => c.id) || []);
      const availableCasesList = cases.filter(caseItem => caseItem.status === 'active' && !assignedCaseIds.has(caseItem.id));
      setAvailableCases(availableCasesList);
      onGroupModalOpen();
    }
  };
  const handleEditGroup = (groupId: string) => {
    const group = groups.find(g => g.id === groupId);
    if (group) {
      setGroupForm({
        name: group.name,
        description: group.description || '',
        is_active: group.is_active
      });
      setViewingItem(group);
      setEditingItem(group);
      setIsEditing(true);
      setIsGroupViewMode(false);
      onGroupModalOpen();
    }
  };
  const handleGroupStatusChange = async (group: Group) => {
    const action = group.is_active ? 'deactivate' : 'reactivate';
    const confirmMessage = group.is_active ? 'Are you sure you want to deactivate this group? Members will lose access.' : 'Are you sure you want to reactivate this group?';
    if (!confirm(confirmMessage)) {
      return;
    }
    setChangingStatusId(group.id);
    setError(null);
    try {
      const method = group.is_active ? 'DELETE' : 'PUT';
      const response = await fetch(`/api/protected/groups/${group.id}/status`, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to ${action} group: ${response.statusText}`);
      }
      await fetchGroups();
      if (viewingItem?.id === group.id) {
        const updatedGroupResponse = await fetch(`/api/protected/groups/${group.id}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        if (updatedGroupResponse.ok) {
          const updatedGroupData = await updatedGroupResponse.json();
          setViewingItem(updatedGroupData.response);
        }
      }
    } catch (err) {
      setOperationalError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setChangingStatusId(null);
    }
  };
  const handleUserStatusChange = async (user: UserDTO, newStatus: 'active' | 'inactive' | 'suspended') => {
    if (!user) return;
    setChangingStatusId(user.id);
    setError(null);
    try {
      let endpoint = `/api/protected/users/${user.id}/status`;
      let method = 'PUT';
      if (newStatus === 'suspended') {
        method = 'DELETE';
      } else if (newStatus === 'inactive') {
        endpoint = `/api/protected/users/${user.id}/deactivate`;
      }
      const response = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to update status: ${response.statusText}`);
      }
      setUser({
        ...user,
        status: newStatus
      });
      setUserFormData({
        ...userFormData,
        status: newStatus
      });
      await fetchUsers();
    } catch (err) {
      setOperationalError(err instanceof Error ? err.message : 'Failed to update user status');
    } finally {
      setChangingStatusId(null);
    }
  };
  const handleAdminStatusChange = async (user: UserDTO) => {
    if (!user) return;
    const action = userFormData.is_admin ? 'revoke' : 'assign';
    const confirmMessage = userFormData.is_admin ? 'Are you sure you want to revoke tenant admin access for this user?' : 'Are you sure you want to assign tenant admin access to this user?';
    if (!confirm(confirmMessage)) {
      return;
    }
    setChangingAccessId(user.id);
    setError(null);
    try {
      let response;
      const url = `/api/protected/users/assign-admin`;
      if (action === 'revoke') {
        response = await fetch(url, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            user_id: user.id,
            role_name: 'tenant_admin'
          })
        });
      } else {
        response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            user_id: user.id
          })
        });
      }
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to ${action} admin access: ${response.statusText}`);
      }
      setUser({
        ...user,
        is_admin: !userFormData.is_admin
      });
      setUserFormData({
        ...userFormData,
        is_admin: !userFormData.is_admin
      });
      await fetchUsers();
    } catch (err) {
      setOperationalError(err instanceof Error ? err.message : 'Failed to update user status');
    } finally {
      setChangingAccessId(null);
    }
  };
  const handlePasswordReset = async (user: UserDTO) => {
    if (!user) return;
    const confirmReset = confirm(`Reset password for ${user.name}?\n\nA new temporary password will be generated and the user will be able to log in immediately. You should communicate the new password to the user securely.`);
    if (!confirmReset) return;
    setResettingPasswordId(user.id);
    setError(null);
    try {
      const endpoint = `/api/protected/users/${user.id}/refresh`;
      const method = 'POST';
      const response = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to reset password: ${response.statusText}`);
      }
      const result = await response.json();

      // Show the new password to the admin
      if (result.temporary_password) {
        alert(`Password reset successful!\n\n` + `User: ${user.email}\n` + `New Password: ${result.temporary_password}\n\n` + `Please securely communicate this password to the user. They can log in immediately with this password.`);
      } else {
        alert('Password reset successful! The user can now log in with their new credentials.');
      }
      await fetchUsers();
    } catch (err) {
      setOperationalError(err instanceof Error ? err.message : 'Failed to reset password');
      alert(`Error: ${err instanceof Error ? err.message : 'Failed to reset password'}`);
    } finally {
      setResettingPasswordId(null);
    }
  };
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  const fetchGroupMembers = async (groupId: string) => {
    try {
      setGroupMembersLoading(true);
      setGroupMembersError(null);

      // Fetch group members
      const membersResponse = await fetch(`/api/protected/groups/${groupId}/members`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      let membersData;
      if (membersResponse.ok) {
        membersData = await membersResponse.json();
        setGroupMembers(membersData.response || []);
      } else {
        throw new Error('Failed to fetch group members');
      }

      // Calculate available users (not already in the group, active only)
      const assignedUserIds = new Set((membersData?.response || []).map((member: GroupMember) => member.user_id));
      const available = users.filter(user => user.status === 'active' && !assignedUserIds.has(user.id));
      setAvailableUsers(available);
    } catch (err) {
      setGroupMembersError(err instanceof Error ? err.message : 'Failed to fetch group members');
    } finally {
      setGroupMembersLoading(false);
    }
  };
  const handleAddMember = async () => {
    if (!selectedUserId || !viewingItem) {
      return;
    }
    try {
      setAssigningUser(true);
      const response = await fetch(`/api/protected/groups/${viewingItem.id}/members`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: selectedUserId
        })
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add member');
      }

      // Reset form and fetch fresh group data
      setSelectedUserId('');

      // Fetch the updated group directly
      const groupResponse = await fetch(`/api/protected/groups/${viewingItem.id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      if (groupResponse.ok) {
        const groupData = await groupResponse.json();
        const updatedGroup = groupData.response;
        setViewingItem(updatedGroup);

        // Recalculate members and available users from fresh data
        if (updatedGroup.users) {
          const membersData = updatedGroup.users.map((user: any) => ({
            id: user.groups?.[0]?.id || user.id,
            user_id: user.id,
            group_id: updatedGroup.id,
            joined_at: user.groups?.[0]?.joined_at || new Date().toISOString(),
            is_active: user.groups?.[0]?.is_active ?? true,
            user: {
              id: user.id,
              email: user.email,
              name: user.name,
              status: user.status
            }
          }));
          setGroupMembers(membersData);
          const assignedUserIds = new Set(updatedGroup.users.map((u: any) => u.id));
          const available = users.filter(user => user.status === 'active' && !assignedUserIds.has(user.id));
          setAvailableUsers(available);
        }

        // Refresh the groups list in the background
        void fetchGroups();
      }
    } catch (err) {
      setGroupMembersError(err instanceof Error ? err.message : 'Failed to add member');
    } finally {
      setAssigningUser(false);
    }
  };
  const handleMemberStatusChange = async (member: GroupMember) => {
    if (!viewingItem) return;
    const action = member.is_active ? 'deactivate' : 'reactivate';
    const confirmMessage = member.is_active ? 'Are you sure you want to deactivate this user?' : 'Are you sure you want to reactivate this user?';
    if (!confirm(confirmMessage)) {
      return;
    }
    setChangingStatusId(member.id);
    setGroupMembersError(null);
    try {
      const method = member.is_active ? 'DELETE' : 'PUT';
      const response = await fetch(`/api/protected/groups/${viewingItem.id}/user-membership/${member.id}/status`, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to ${action} user membership: ${response.statusText}`);
      }
      await fetchGroupMembers(viewingItem.id);
    } catch (err) {
      setGroupMembersError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setChangingStatusId(null);
    }
  };
  const fetchGroupCases = async (groupId: string) => {
    try {
      setGroupCasesLoading(true);
      setGroupCasesError(null);

      // Fetch group cases
      const assignmentsResponse = await fetch(`/api/protected/groups/${groupId}/case-assignments`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      let assignmentsData;
      if (assignmentsResponse.ok) {
        assignmentsData = await assignmentsResponse.json();
        setGroupCases(assignmentsData.response || []);
      } else {
        throw new Error('Failed to fetch group cases');
      }

      // Calculate available cases (not already assigned to the group)
      const assignedCaseIds = new Set((assignmentsData?.response || []).map((assignment: GroupCase) => assignment.case_id));
      const available = cases.filter(caseItem => caseItem.status === 'active' && !assignedCaseIds.has(caseItem.id));
      setAvailableCases(available);
    } catch (err) {
      setGroupCasesError(err instanceof Error ? err.message : 'Failed to fetch group cases');
    } finally {
      setGroupCasesLoading(false);
    }
  };
  const handleAddCase = async () => {
    if (!selectedCaseId || !selectedRole || !viewingItem) {
      return;
    }
    try {
      setAssigningCase(true);
      const response = await fetch(`/api/protected/groups/${viewingItem.id}/case-assignments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          case_id: selectedCaseId,
          case_role: selectedRole
        })
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add case');
      }

      // Reset form and fetch fresh group data
      setSelectedCaseId('');
      setSelectedRole('member');

      // Fetch the updated group directly
      const groupResponse = await fetch(`/api/protected/groups/${viewingItem.id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      if (groupResponse.ok) {
        const groupData = await groupResponse.json();
        const updatedGroup = groupData.response;
        setViewingItem(updatedGroup);

        // Recalculate cases and available cases from fresh data
        if (updatedGroup.cases) {
          const casesData = updatedGroup.cases.map((caseItem: any) => ({
            id: caseItem.id,
            case_id: caseItem.id,
            group_id: updatedGroup.id,
            case_role: caseItem.case_role || 'member',
            assigned_at: caseItem.assigned_at || caseItem.created_at || new Date().toISOString(),
            is_active: caseItem.status === 'active',
            case: {
              id: caseItem.id,
              name: caseItem.name,
              status: caseItem.status,
              case_number: caseItem.case_number,
              description: caseItem.description || ''
            }
          }));
          setGroupCases(casesData);
          const assignedCaseIds = new Set(updatedGroup.cases.map((c: any) => c.id));
          const availableCasesList = cases.filter(caseItem => caseItem.status === 'active' && !assignedCaseIds.has(caseItem.id));
          setAvailableCases(availableCasesList);
        }

        // Refresh the groups list in the background
        void fetchGroups();
      }
    } catch (err) {
      setGroupCasesError(err instanceof Error ? err.message : 'Failed to add case');
    } finally {
      setAssigningCase(false);
    }
  };
  const handleCaseStatusChange = async (assignment: GroupCase) => {
    if (!viewingItem) return;
    const action = assignment.is_active ? 'deactivate' : 'reactivate';
    const confirmMessage = assignment.is_active ? 'Are you sure you want to deactivate this case assignment? The group will lose access to this case.' : 'Are you sure you want to reactivate this case assignment?';
    if (!confirm(confirmMessage)) {
      return;
    }
    setChangingCaseStatusId(assignment.id);
    setGroupCasesError(null);
    try {
      const method = assignment.is_active ? 'DELETE' : 'PUT';
      const response = await fetch(`/api/protected/groups/${viewingItem.id}/case-assignments/${assignment.id}/status`, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to ${action} case assignment: ${response.statusText}`);
      }
      await fetchGroupCases(viewingItem.id);
      const updatedGroupResponse = await fetch(`/api/protected/groups/${viewingItem.id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      if (updatedGroupResponse.ok) {
        const updatedGroupData = await updatedGroupResponse.json();
        setViewingItem(updatedGroupData.response);
      }
    } catch (err) {
      setGroupCasesError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setChangingCaseStatusId(null);
    }
  };
  const handleUserSubmit = async () => {
    if (!validateUserForm()) {
      return;
    }
    try {
      setSaving(true);
      setError(null);
      let response;
      let userId;
      if (isEditing && viewingItem?.id) {
        userId = viewingItem.id;
        // Update existing user
        response = await fetch(`/api/protected/users/${userId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: userFormData.name,
            email: userFormData.email
          })
        });
      } else {
        // Create new user
        const payload = {
          name: userFormData.name,
          email: userFormData.email,
          ...(userFormData.password ? {
            temporary_password: userFormData.password
          } : {})
        };
        response = await fetch('/api/protected/users', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
        if (response.ok) {
          const responseData = await response.json();
          userId = responseData.response?.id;
        }
      }
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to ${isEditing ? 'update' : 'create'} user: ${response.statusText}`);
      }

      // If admin privileges requested, assign tenant admin role
      if (userFormData.is_admin && userId) {
        const assignAdminResponse = await fetch('/api/protected/users/assign-admin', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            user_id: userId,
            role_name: 'tenant_admin'
          })
        });
        if (!assignAdminResponse.ok) {
          const errorData = await assignAdminResponse.json();
          console.warn('User saved but admin role assignment failed:', errorData.error);
          setOperationalError(`User ${isEditing ? 'updated' : 'created'} successfully, but failed to assign admin role: ${errorData.error}`);
        }
      }
      onUserModalOpenChange();
      void fetchUsers();
      setSaving(false);
    } catch (err) {
      console.error('Error submitting user form:', err);
      setOperationalError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setSaving(false);
    }
  };
  const handleCaseSubmit = async () => {
    try {
      if (!validateCaseForm()) {
        return;
      }
      setSaving(true);
      setError(null);
      let transferOwnership = false;
      const payload: Record<string, string> = {
        name: caseFormData.name
      };
      if (caseFormData.description) {
        payload.description = caseFormData.description;
      }
      if (caseFormData.case_number) {
        payload.case_number = caseFormData.case_number;
      }
      if (caseFormData.owner_id) {
        payload.owner_id = caseFormData.owner_id;
        if (viewingItem?.id && viewingItem?.owner_id !== caseFormData.owner_id) {
          transferOwnership = true;
        }
      }
      let caseId;
      // let response;
      let url = '/api/protected/cases';
      let method = 'POST';
      if (viewingItem?.id) {
        caseId = viewingItem.id;
        url = `${url}/${caseId}`;
        method = 'PUT';
      }
      // Update existing user
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create case');
      }
      if (transferOwnership) {
        const transferResponse = await fetch(`/api/protected/cases/${viewingItem.id}/ownership-transfer`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            new_owner_id: caseFormData.owner_id
          })
        });
        if (!transferResponse.ok) {
          const errorData = await transferResponse.json().catch(() => ({}));
          throw new Error(errorData.detail || errorData.error || 'Failed to transfer ownership');
        }
      }
      onCaseModalOpenChange();
      await fetchCases();
      // Reset form state and clear validation errors
      setCaseFormData({
        name: '',
        description: '',
        owner_id: ''
      });
      setEditingItem(null);
      setIsEditing(false);
      setValidationErrors({});
    } catch (err) {
      console.error('Error submitting case form:', err);
      setOperationalError(err instanceof Error ? err.message : 'Failed to create case');
    } finally {
      setSaving(false);
    }
  };
  const validateCaseForm = (): boolean => {
    const errors: ValidationErrors = {};
    if (!caseFormData.name.trim()) {
      errors.name = 'Case name is required';
    } else if (caseFormData.name.length < 3) {
      errors.name = 'Case name must be at least 3 characters';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };
  const validateGroupForm = (): boolean => {
    const errors: ValidationErrors = {};
    if (!groupForm.name.trim()) {
      errors.name = 'Group name is required';
    } else if (groupForm.name.length < 3) {
      errors.name = 'Group name must be at least 3 characters';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };
  const validateUserForm = (): boolean => {
    const errors: {
      [key: string]: string;
    } = {};
    if (!userFormData.name.trim()) {
      errors.name = 'Name is required';
    }
    if (!userFormData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userFormData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    if (userFormData.password && userFormData?.password.trim().length > 0 && userFormData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters long';
    }
    if (userFormData.password !== userFormData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };
  const handleGroupInputChange = (field: keyof GroupFormData, value: string) => {
    setGroupForm(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev: ValidationErrors) => ({
        ...prev,
        [field]: undefined
      }));
    }
  };
  const handleGroupSubmit = async () => {
    try {
      if (!validateGroupForm()) {
        return;
      }
      setSaving(true);
      setError(null);
      const submitData = {
        name: groupForm.name.trim(),
        description: groupForm.description.trim() || undefined
      };
      const isUpdate = isEditing && viewingItem?.id;
      const url = isUpdate ? `/api/protected/groups/${viewingItem.id}` : '/api/protected/groups';
      const method = isUpdate ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to ${isUpdate ? 'update' : 'create'} group: ${response.statusText}`);
      }
      onGroupModalClose();
      void fetchGroups();
      setSaving(false);
    } catch (err) {
      console.error('Error submitting group form:', err);
      setOperationalError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setSaving(false);
    }
  };
  const fetchCaseUsers = async (caseId: string) => {
    try {
      setCaseUsersLoading(true);
      setCaseUsersError(null);
      const [caseResponse, usersResponse] = await Promise.all([fetch(`/api/protected/cases/${caseId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      }), fetch('/api/protected/users', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })]);
      if (!caseResponse.ok) {
        throw new Error('Failed to fetch case details');
      }
      if (!usersResponse.ok) {
        throw new Error('Failed to fetch users');
      }
      const caseData = await caseResponse.json();
      const usersData = await usersResponse.json();
      const caseUsersWithDetails = (caseData.response.users_with_access || []).map((cu: any) => {
        return {
          ...cu,
          user_id: cu.id,
          user_name: cu.name,
          user_email: cu.email,
          case_role: cu.access_level,
          access_type: cu.access_type,
          group_id: cu.group_id,
          assignment_id: cu.assignment_id
        };
      });
      setCaseUsers(caseUsersWithDetails);
      const assignedUserIds = new Set(caseUsersWithDetails.map((cu: CaseUserRole) => cu.user_id));
      const available = usersData.response.filter((u: UserDTO) => u.status === 'active' && !assignedUserIds.has(u.id));
      setAvailableCaseUsers(available);
    } catch (err) {
      setCaseUsersError(err instanceof Error ? err.message : 'Failed to load case users');
    } finally {
      setCaseUsersLoading(false);
    }
  };
  const handleAddCaseUser = async () => {
    if (!selectedCaseUserId || !viewingItem) {
      return;
    }
    try {
      setAssigningCaseUser(true);
      const response = await fetch('/api/protected/user-case-roles', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: selectedCaseUserId,
          case_id: viewingItem.id,
          case_role: selectedCaseUserRole,
          notes: ''
        })
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to assign user');
      }
      await fetchCaseUsers(viewingItem.id);
      onAddCaseUserModalOpenChange();
      setSelectedCaseUserId('');
      setSelectedCaseUserRole('member');
    } catch (err) {
      setCaseUsersError(err instanceof Error ? err.message : 'Failed to assign user');
    } finally {
      setAssigningCaseUser(false);
    }
  };
  const handleRemoveCaseUser = async (userId: string, accessType: string, groupId?: string, assignment_id?: string) => {
    if (!viewingItem) return;
    if (!confirm('Are you sure you want to remove this user from the case?')) {
      return;
    }
    try {
      let response;
      switch (accessType) {
        case 'case_role_access':
          response = await fetch(`/api/protected/user-case-roles/${userId}/${viewingItem.id}`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json'
            }
          });
          break;
        case 'group_access':
          if (!groupId) {
            throw new Error('Group ID required for group access removal');
          }
          if (!userId) {
            throw new Error('User ID required for group access removal');
          }
          response = await fetch(`/api/protected/groups/${groupId}/user-membership/${assignment_id}/status`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json'
            }
          });
          break;
        case 'owner_access':
          response = await fetch(`/api/protected/cases/${viewingItem.id}/ownership-transfer`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({})
          });
          break;
        default:
          throw new Error(`Unknown access type: ${accessType}`);
      }
      if (!response.ok) {
        throw new Error('Failed to remove user');
      }
      await fetchCaseUsers(viewingItem.id);
    } catch (err) {
      setCaseUsersError(err instanceof Error ? err.message : 'Failed to remove user');
    }
  };
  const handleAssignTenantAdmin = async () => {
    if (!selectedUserForAdmin) return;
    try {
      setAssigningAdmin(true);
      const response = await fetch('/api/protected/users/assign-admin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: selectedUserForAdmin,
          role_name: 'tenant_admin'
        })
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || errorData.error || 'Failed to assign tenant admin role');
      }
      setTenantAdminError(null);
      onTenantAdminModalOpenChange();
      setSelectedUserForAdmin('');
      await fetchUsers();
    } catch (err) {
      setTenantAdminError(err instanceof Error ? err.message : 'Failed to assign tenant admin');
    } finally {
      setAssigningAdmin(false);
    }
  };
  if (loading) {
    return <div className="min-h-screen bg-[#0F1D32] bg-gradient-to-br from-[#091523] via-[#0C1D30] to-[#0F1D32] flex items-center justify-center">
        <div className="text-center">
          <Spinner size="lg" className="text-white mx-auto mb-4" />
          <div className="text-white text-2xl font-semibold">Loading account settings...</div>
        </div>
      </div>;
  }
  if (!isAdmin) {
    return <div className="min-h-screen bg-[#0F1D32] bg-gradient-to-br from-[#091523] via-[#0C1D30] to-[#0F1D32] flex items-center justify-center">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <h1 className="text-2xl font-bold text-red-400">Access Denied</h1>
          </CardHeader>
          <CardBody className="text-center">
            <p role="alert" aria-live="assertive" className="text-gray-300 mb-4">
              {error || 'You do not have admin privileges to access this panel.'}
            </p>
          </CardBody>
        </Card>
      </div>;
  }
  return <div className="min-h-screen bg-[#0F1D32] bg-gradient-to-br from-[#091523] via-[#0C1D30] to-[#0F1D32]" data-sentry-component="AdminPanel" data-sentry-source-file="AdminPanel.tsx">
      <div className="max-w-7xl mx-auto">
        <div className="box-border flex flex-col gap-4 my-8">
          {/* Operational Error Alert */}
          {operationalError && <div className="p-4 bg-red-900/20 border border-red-500/50 rounded-lg flex items-start justify-between">
              <div className="flex items-start gap-3">
                <span className="text-red-400 text-xl">⚠️</span>
                <div>
                  <h3 className="text-red-400 font-semibold mb-1">Operation Failed</h3>
                  <p className="text-red-300 text-sm">{operationalError}</p>
                </div>
              </div>
              <button onClick={() => setOperationalError(null)} className="text-red-400 hover:text-red-300 transition-colors" aria-label="Dismiss error">
                ✕
              </button>
            </div>}
          
          <Button variant="light" onPress={() => onBackToDashboard ? onBackToDashboard() : router.push('/')} className="w-fit text-gray-400 hover:text-white hover:bg-white/10 transition-colors" startContent={<ArrowLeftIcon size={20} />} aria-label="Back to dashboard" data-sentry-element="Button" data-sentry-source-file="AdminPanel.tsx">
            Back to Dashboard
          </Button>
          <div className="box-border flex flex-col m-0 p-0 gap-1">
            <h1 className="text-3xl font-light text-white m-0 p-0">Account Settings</h1>
            <p className="text-gray-500 m-0 p-0 text-sm">Manage your account settings and preferences</p>
          </div>
        </div>
        <Tabs aria-label="Account Settings" color="primary" variant="solid" size="lg" radius="full" classNames={{
        tabList: "gap-2 w-full relative rounded-full bg-white/15 mb-4",
        cursor: "w-full bg-brand-blue-light/20",
        tab: "hover:bg-white/10 transition-all duration-300",
        tabContent: "group-data-[selected=true]:text-white hover:text-white"
      }} selectedKey={selectedTab} onSelectionChange={key => {
        setSelectedTab(key as string);
      }} data-sentry-element="Tabs" data-sentry-source-file="AdminPanel.tsx">
            <Tab key="users" title={<div className="flex items-center gap-2">
                    <UserIcon weight="duotone" size={25} />
                    <span>Users</span>
                  </div>} data-sentry-element="Tab" data-sentry-source-file="AdminPanel.tsx" />
            {enableCaseManagement && <Tab key="cases" title={<div className="flex items-center gap-2">
                      <BriefcaseMetalIcon weight="duotone" size={25} />
                      <span>Cases</span>
                    </div>} />}
            <Tab key="groups" title={<div className="flex items-center gap-2">
                    <UsersThreeIcon weight="duotone" size={25} />
                    <span>Groups</span>
                  </div>} data-sentry-element="Tab" data-sentry-source-file="AdminPanel.tsx" />
            {(isSuperAdmin || canManageOrgs) && <Tab key="organizations" title={<div className="flex items-center gap-2">
                    <BuildingsIcon weight="duotone" size={25} />
                    <span>Organizations</span>
                  </div>} />}
            {(isSuperAdmin || canManageOrgs) && <Tab key="tenants" title={<div className="flex items-center gap-2">
                        <BuildingsIcon weight="duotone" size={25} />
                        <span>Tenants</span>
                      </div>} />}
          </Tabs>
          {/* Tab Content */}
          {selectedTab === 'users' && <>
              {/* Users Content */}
              {usersLoading ? <LoadingCard message="Loading users..." /> : usersError ? <Card className="w-full bg-white/5 border-white/10">
                  <CardBody className="flex flex-col items-center justify-center text-center">
                    <p role="alert" aria-live="assertive" className="text-red-400 mb-4">Error loading users: {usersError}</p>
                     <Button color="primary" onPress={fetchUsers} className="w-fit text-white hover:bg-white/10 cursor-pointer">
                      Retry
                    </Button>
                  </CardBody>
                </Card> : users.length === 0 ? <Card className="max-w-full overflow-visible bg-white/5 border-white/10">
                  <CardBody className="text-center">
                    <UserCircleIcon size={64} className="text-gray-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-white mb-2">No Users Found</h3>
                    <p className="text-gray-300 mb-6">
                      There are no users in this tenant yet. Create your first user to get started.
                    </p>
                     <Button color="primary" onPress={handleCreateUser} startContent={<PlusIcon size={20} />} className="cursor-pointer">
                      Create First User
                    </Button>
                  </CardBody>
                </Card> : <Card classNames={{
          base: "box-border bg-white/5 border-white/10 p-4 rounded-lg h-[calc(100vh-300px)]",
          header: "box-border flex justify-between items-center bg-white/5 p-4 rounded-lg",
          body: "box-border pt-6"
        }}>
                  <CardHeader>
                    <div className="box-border flex justify-between items-center w-full">
                      <div className="box-border flex flex-col m-0 p-0 gap-1">
                        <h3 className="text-xl font-semibold text-white m-0 p-0">Users</h3>
                        <span className="text-gray-400 m-0 p-0 text-sm italic max-w-[350px]">Create new users; manage existing users; assign roles and permissions; view user activity; and more.</span>
                      </div>
                      <Button color="secondary" onPress={onTenantAdminModalOpen} startContent={<ShieldCheckIcon size={20} />} size="sm" className="cursor-pointer">
                        Assign Tenant Admin
                      </Button>
                       <Button onPress={handleCreateUser} startContent={<PlusIcon size={20} />} className="text-white bg-brand-blue-light/20 hover:bg-white/10 cursor-pointer">
                        Add User
                      </Button>
                    </div>
                  </CardHeader>
                  <CardBody>
                     <Table aria-label="Users table" classNames={{
              base: "box-border bg-transparent shadow-none h-full",
              wrapper: "box-border p-0 m-0 bg-transparent shadow-none overflow-none ",
              table: "bg-transparent shadow-none p-0 m-0 rounded-none w-full",
              th: "bg-white/25 text-gray-300 tracking-widest hover:bg-white/25",
              tr: "hover:bg-brand-blue-light/20",
              td: "border-b-1 border-white/10 border-solid"
            }}>
                      <TableHeader>
                        <TableColumn>USER</TableColumn>
                        <TableColumn>STATUS</TableColumn>
                        <TableColumn>ROLE</TableColumn>
                        <TableColumn>CASES</TableColumn>
                        <TableColumn>GROUPS</TableColumn>
                      </TableHeader>
                      <TableBody>
                        {users.map(user => <TableRow key={user.id} className="hover:bg-white/5 transition-colors">
                            <TableCell>
                              <div className="flex flex-col gap-1 cursor-pointer h-full min-h-[60px] justify-center" onClick={() => handleViewUser(user.id)}>
                                <div className="flex items-center gap-2">
                                  <div className="font-semibold text-white">{user.name}</div>
                                  {user.cognito_needs_password_reset && <Tooltip content={`Password expired (${user.cognito_days_since_creation} days). Click to reset.`} placement="top" delay={300} showArrow={true} classNames={{
                          content: 'bg-orange-600 text-white px-3 py-2 rounded-md text-sm max-w-[300px] break-words',
                          base: 'before:bg-orange-600 before:rounded-[2px]'
                        }}>
                                      <button onClick={e => {
                            e.stopPropagation();
                            handlePasswordReset(user);
                          }} className="flex items-center">
                                        <WarningOctagonIcon size={20} weight="fill" className="text-orange-500 hover:text-orange-400 transition-colors" />
                                      </button>
                                    </Tooltip>}
                                </div>
                                <div className="text-sm text-gray-500 italic">{user.email}</div>
                              </div>
                            </TableCell>
                             <TableCell>
                               <div className="text-sm text-gray-300 cursor-pointer h-full min-h-[60px] flex items-center" onClick={() => handleViewUser(user.id)}>
                                 {user.status}
                               </div>
                             </TableCell>
                             <TableCell>
                               <div className="text-sm text-gray-300 cursor-pointer h-full min-h-[60px] flex items-center" onClick={() => handleViewUser(user.id)}>
                                 {user.is_admin ? 'Admin' : 'User'}
                               </div>
                             </TableCell>
                            <TableCell>
                              <div className="text-sm text-gray-300 cursor-pointer h-full min-h-[60px] flex items-center" onClick={() => handleViewUser(user.id)}>
                                {user.cases?.length || 0} cases
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm text-gray-300 cursor-pointer h-full min-h-[60px] flex items-center" onClick={() => handleViewUser(user.id)}>
                                {user.groups?.length || 0} groups
                              </div>
                            </TableCell>

                          </TableRow>)}
                      </TableBody>
                    </Table>
                  </CardBody>
                </Card>}
            </>}

           {enableCaseManagement && selectedTab === 'cases' && <>
               {/* Cases Content */}
               {casesLoading ? <LoadingCard message="Loading cases..." /> : casesError ? <Card className="w-full bg-white/5 border-white/10">
                   <CardBody className="flex flex-col items-center justify-center text-center">
                     <p role="alert" aria-live="assertive" className="text-red-400 mb-4">Error loading cases: {casesError}</p>
                     <Button color="primary" onPress={() => fetchCases()} className="w-fit text-white hover:bg-white/10 cursor-pointer">
                       Retry
                     </Button>
                   </CardBody>
                 </Card> : cases.length === 0 ? <Card className="max-w-full overflow-visible bg-white/5 border-white/10">
                   <CardBody className="text-center">
                     <FolderIcon size={64} className="text-gray-400 mx-auto mb-4" />
                     <h3 className="text-xl font-semibold text-white mb-2">No Cases Found</h3>
                     <p className="text-gray-300 mb-6">
                       There are no cases in this tenant yet. Sync cases from DynamoDB to get started.
                     </p>
                    <Button color="primary" onPress={handleSyncCases} startContent={<ArrowsClockwiseIcon size={20} />} isDisabled={casesLoading} isLoading={casesLoading} className="cursor-pointer">
                      Sync Cases
                    </Button>
                   </CardBody>
                 </Card> : <Card classNames={{
          base: "box-border bg-white/5 border-white/10 p-4 rounded-lg h-[calc(100vh-300px)]",
          header: "box-border flex justify-between items-center bg-white/5 p-4 rounded-lg",
          body: "box-border pt-6"
        }}>
                   <CardHeader>
                     <div className="box-border flex justify-between items-center w-full">
                       <div className="box-border flex flex-col m-0 p-0 gap-1">
                         <h3 className="text-xl font-semibold text-white m-0 p-0">Cases</h3>
                         <span className="text-gray-400 m-0 p-0 text-sm italic max-w-[350px]">Sync cases from DynamoDB; manage existing cases; assign users and permissions; view case activity; and more.</span>
                       </div>
                       <Button onPress={handleSyncCases} startContent={<ArrowsClockwiseIcon size={20} />} isDisabled={casesLoading} isLoading={casesLoading} className="text-white bg-brand-blue-light/20 hover:bg-white/10 cursor-pointer">
                         Sync Cases
                       </Button>
                     </div>
                   </CardHeader>
                   <CardBody>
                     <Table aria-label="Cases table" classNames={{
              base: "box-border bg-transparent shadow-none h-full",
              wrapper: "box-border p-0 m-0 bg-transparent shadow-none overflow-none ",
              table: "bg-transparent shadow-none p-0 m-0 rounded-none w-full",
              th: "bg-white/25 text-gray-300 tracking-widest hover:bg-white/25",
              tr: "hover:bg-brand-blue-light/20",
              td: "border-b-1 border-white/10 border-solid"
            }}>
                       <TableHeader>
                         <TableColumn>CASE</TableColumn>
                         <TableColumn>STATUS</TableColumn>
                         <TableColumn>USERS</TableColumn>
                         <TableColumn>GROUPS</TableColumn>
                         <TableColumn>CREATED</TableColumn>
                         <TableColumn>UPDATED</TableColumn>
                       </TableHeader>
                       <TableBody>
                         {cases.map(caseItem => <TableRow key={caseItem.id} className="hover:bg-white/5 transition-colors">
                             <TableCell>
                               <div className="flex flex-col gap-1 cursor-pointer h-full min-h-[60px] justify-center" onClick={() => handleViewCase(caseItem.id)}>
                                 <div className="font-semibold text-white">{caseItem.name}</div>
                                 {caseItem.description && <div className="text-sm text-gray-500 italic truncate max-w-xs">
                                     {caseItem.description}
                                   </div>}
                               </div>
                             </TableCell>
                             <TableCell>
                               <div className="text-sm text-gray-300 cursor-pointer h-full min-h-[60px] flex items-center" onClick={() => handleViewCase(caseItem.id)}>
                                 {caseItem.status}
                               </div>
                             </TableCell>
                             <TableCell>
                               <div className="text-sm text-gray-300 cursor-pointer h-full min-h-[60px] flex items-center" onClick={() => handleViewCase(caseItem.id)}>
                                 {(caseItem.owner ? 1 : 0) + (caseItem.user_roles?.length || 0)} users
                               </div>
                             </TableCell>
                             <TableCell>
                               <div className="text-sm text-gray-300 cursor-pointer h-full min-h-[60px] flex items-center" onClick={() => handleViewCase(caseItem.id)}>
                                 {caseItem.group_assignments?.length || 0} groups
                               </div>
                             </TableCell>
                             <TableCell>
                               <div className="text-sm text-gray-300 cursor-pointer h-full min-h-[60px] flex items-center" onClick={() => handleViewCase(caseItem.id)}>
                                 {formatDate(caseItem.created_at)}
                               </div>
                             </TableCell>
                             <TableCell>
                               <div className="text-sm text-gray-300 cursor-pointer h-full min-h-[60px] flex items-center" onClick={() => handleViewCase(caseItem.id)}>
                                 {formatDate(caseItem.updated_at)}
                               </div>
                             </TableCell>
                           </TableRow>)}
                       </TableBody>
                     </Table>
                   </CardBody>
                 </Card>}
             </>}

          {selectedTab === 'organizations' && (isSuperAdmin || canManageOrgs) && <OrganizationsTab />}
          {selectedTab === 'tenants' && (isSuperAdmin || canManageOrgs) && <TenantsTab />}


        {selectedTab === 'groups' && <>
              {/* Groups Content */}
              {groupsLoading ? <LoadingCard message="Loading groups..." /> : groupsError ? <Card className="w-full bg-white/5 border-white/10">
                  <CardBody className="flex flex-col items-center justify-center text-center">
                    <p role="alert" aria-live="assertive" className="text-red-400 mb-4">Error loading groups: {groupsError}</p>
                     <Button color="primary" onPress={fetchGroups} className="w-fit text-white hover:bg-white/10 cursor-pointer">
                      Retry
                    </Button>
                  </CardBody>
                </Card> : <>
                  {/* Search bar always visible when groups tab has data, so users can change search from empty state */}
                  <Input aria-label="Search groups by name or description" placeholder="Search groups by name or description..." startContent={<MagnifyingGlassIcon size={18} className="text-gray-500" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} classNames={{
            base: "w-full mb-4",
            mainWrapper: "box-border w-full",
            input: "text-white",
            inputWrapper: "box-border bg-white/5 border-white/10"
          }} />
                  {filteredGroups.length === 0 ? <Card className="max-w-full overflow-visible bg-white/5 border-white/10">
                      <CardBody className="text-center">
                        <UsersIcon size={64} className="text-gray-400 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-white mb-2">
                          {searchTerm ? 'No Groups Found' : 'No Groups Found'}
                        </h3>
                        <p className="text-gray-300 mb-6">
                          {searchTerm ? 'Try adjusting your search terms' : 'There are no groups in this tenant yet. Create your first group to get started.'}
                        </p>
                        {!searchTerm && <Button color="primary" onPress={handleCreateGroup} startContent={<PlusIcon size={20} />} className="cursor-pointer">
                            Create First Group
                          </Button>}
                      </CardBody>
                    </Card> : <Card classNames={{
            base: "box-border bg-white/5 border-white/10 p-4 rounded-lg h-[calc(100vh-300px)]",
            header: "box-border flex justify-between items-center bg-white/5 p-4 rounded-lg",
            body: "box-border pt-6"
          }}>
                  <CardHeader>
                    <div className="box-border flex justify-between items-center w-full">
                      <div className="box-border flex flex-col m-0 p-0 gap-1">
                        <h3 className="text-xl font-semibold text-white m-0 p-0">Groups</h3>
                        <span className="text-gray-400 m-0 p-0 text-sm italic max-w-[350px]">Create new groups; manage existing groups; assign members and permissions; view group activity; and more.</span>
                      </div>
                       <Button onPress={handleCreateGroup} startContent={<PlusIcon size={20} />} className="text-white bg-brand-blue-light/20 hover:bg-white/10 cursor-pointer">
                        Add Group
                      </Button>
                    </div>
                  </CardHeader>
                  <CardBody>
                     <Table aria-label="Groups table" classNames={{
                base: "box-border bg-transparent shadow-none h-full",
                wrapper: "box-border p-0 m-0 bg-transparent shadow-none overflow-none ",
                table: "bg-transparent shadow-none p-0 m-0 rounded-none w-full",
                th: "bg-white/25 text-gray-300 tracking-widest hover:bg-white/25",
                tr: "hover:bg-brand-blue-light/20",
                td: "border-b-1 border-white/10 border-solid"
              }}>
                      <TableHeader>
                        <TableColumn>NAME</TableColumn>
                        <TableColumn>DESCRIPTION</TableColumn>
                        <TableColumn>STATUS</TableColumn>
                        <TableColumn>MEMBERS</TableColumn>
                        <TableColumn>CASES</TableColumn>
                        <TableColumn>CREATED</TableColumn>
                      </TableHeader>
                      <TableBody>
                        {filteredGroups.map(group => <TableRow key={group.id} className="hover:bg-white/5 transition-colors">
                            <TableCell>
                              <div className="flex flex-col gap-1 cursor-pointer h-full min-h-[60px] justify-center" onClick={() => handleViewGroup(group.id)}>
                                <div className="font-semibold text-white">{group.name}</div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm text-gray-500 italic max-w-xs truncate cursor-pointer h-full min-h-[60px] flex items-center" onClick={() => handleViewGroup(group.id)}>
                                {group.description || 'No description'}
                              </div>
                            </TableCell>
                             <TableCell>
                               <div className="text-sm text-gray-300 cursor-pointer h-full min-h-[60px] flex items-center" onClick={() => handleViewGroup(group.id)}>
                                 {group.is_active ? 'Active' : 'Inactive'}
                               </div>
                             </TableCell>
                            <TableCell>
                              <div className="text-sm text-gray-300 cursor-pointer h-full min-h-[60px] flex items-center" onClick={() => handleViewGroup(group.id)}>
                                {group.users?.length || 0} members
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm text-gray-300 cursor-pointer h-full min-h-[60px] flex items-center" onClick={() => handleViewGroup(group.id)}>
                                {group.cases?.length || 0} cases
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm text-gray-300 cursor-pointer h-full min-h-[60px] flex items-center" onClick={() => handleViewGroup(group.id)}>
                                {formatDate(group.created_at)}
                              </div>
                            </TableCell>
                          </TableRow>)}
                      </TableBody>
                    </Table>
                  </CardBody>
                </Card>}
                </>}
            </>}
        </div>

         {/* User Modal */}
         <Modal isOpen={isUserModalOpen} onOpenChange={open => {
      if (!open) resetUserModalState();
      onUserModalOpenChange();
    }} size="2xl" classNames={{
      base: "box-border bg-gray-800 border-white/10",
      header: "box-border",
      body: "box-border py-6",
      footer: "box-border px-4",
      closeButton: "text-2xl hover:bg-white/0 hover:text-white cursor-pointer pt-3.5"
    }} data-sentry-element="Modal" data-sentry-source-file="AdminPanel.tsx">
          <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="AdminPanel.tsx">
            {onClose => <>
                 <ModalHeader className="flex bg-white/10 px-6 py-4 uppercase tracking-widest">
                   {isViewMode && viewingItem ? <div className="flex items-center gap-4">
                       <span>{viewingItem.name}</span>
                       <Tooltip content="Edit user" placement="top" delay={600} showArrow={true} classNames={{
                content: 'bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm max-w-[300px] break-words',
                base: 'before:bg-tooltip-bg before:rounded-[2px] before:shadow-none before:drop-shadow-none '
              }}>
                        <Button variant="light" size="sm" onPress={() => {
                  setIsViewMode(false);
                  setIsEditing(true);
                  setUserFormData({
                    name: viewingItem.name,
                    email: viewingItem.email,
                    status: viewingItem.status,
                    is_admin: viewingItem.is_admin,
                    password: '',
                    confirmPassword: ''
                  });
                  setEditingItem(viewingItem);
                }} className="cursor-pointer text-white hover:bg-white/10" aria-label="Edit user" isIconOnly>
                           <PencilIcon size={20} />
                         </Button>
                       </Tooltip>
                       <Tooltip content="Reset Password" placement="top" delay={600} showArrow={true} classNames={{
                content: 'bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm max-w-[300px] break-words',
                base: 'before:bg-tooltip-bg before:rounded-[2px] before:shadow-none before:drop-shadow-none '
              }}>
                        <Button variant="light" size="sm" onPress={() => handlePasswordReset(viewingItem)} isDisabled={resettingPasswordId !== null} isLoading={resettingPasswordId === viewingItem?.id} className="cursor-pointer text-white hover:bg-white/10" aria-label="Reset password" isIconOnly>
                           <KeyIcon size={20} />
                         </Button>
                       </Tooltip>
                     </div> : isEditing ? 'Edit User' : 'Add User'}
                 </ModalHeader>
                <ModalBody>
                  {isViewMode && viewingItem ? <div className="grid grid-cols-2 gap-6 mt-4">
                      <div className="flex flex-row gap-2">
                        <span className="font-semibold text-white m-0 p-0">Email:</span>
                        <span className="text-gray-300">{viewingItem.email}</span>
                      </div>
                      <div className="flex flex-row gap-2">
                        <span className="font-semibold text-white m-0 p-0">Status:</span>
                        <span className="text-gray-300">{viewingItem.status}</span>
                      </div>
                      <div className="flex flex-row gap-2">
                        <span className="font-semibold text-white m-0 p-0">Role:</span>
                        <span className="text-gray-300">{viewingItem.is_admin ? 'Admin' : 'User'}</span>
                      </div>
                      <div className="flex flex-row gap-2">
                        <span className="font-semibold text-white m-0 p-0">Cases:</span>
                        <span className="text-gray-300">{viewingItem.cases?.length || 0} cases</span>
                      </div>
                      <div className="flex flex-row gap-2">
                        <span className="font-semibold text-white m-0 p-0">Groups:</span>
                        <span className="text-gray-300">{viewingItem.groups?.length || 0} groups</span>
                      </div>
                      <div className="flex flex-row gap-2">
                        <span className="font-semibold text-white m-0 p-0 whitespace-nowrap">User ID:</span>
                        <span className="text-gray-300 text-sm font-mono">{viewingItem.id}</span>
                      </div>
                    </div> : <div className="flex flex-col gap-4">
                      <Input label="Name" placeholder="Enter user name" value={userFormData.name} onChange={e => handleUserInputChange('name', e.target.value)} isInvalid={!!validationErrors.name} errorMessage={validationErrors.name} isRequired aria-required="true" classNames={{
                input: "box-border text-white",
                inputWrapper: "box-border"
              }} />
                      <Input label="Email" placeholder="Enter user email" type="email" value={userFormData.email} onChange={e => handleUserInputChange('email', e.target.value)} isInvalid={!!validationErrors.email} errorMessage={validationErrors.email} isRequired aria-required="true" classNames={{
                input: "box-border text-white",
                inputWrapper: "box-border"
              }} />
                      {!isEditing && <>
                          <Input label="Password" placeholder="Enter secure password" type={showPassword ? "text" : "password"} value={userFormData.password || ''} onChange={e => handleUserInputChange('password', e.target.value)} isInvalid={!!validationErrors.password} errorMessage={validationErrors.password} endContent={<button className="focus:outline-none" type="button" onClick={() => setShowPassword(!showPassword)}>
                                {showPassword ? <EyeSlashIcon size={20} className="text-gray-400 pointer-events-none" /> : <EyeIcon size={20} className="text-gray-400 pointer-events-none" />}
                              </button>} classNames={{
                  input: "box-border text-white",
                  inputWrapper: "box-border"
                }} />
                          <Input label="Confirm Password" placeholder="Confirm password" type={showConfirmPassword ? "text" : "password"} value={userFormData.confirmPassword || ''} onChange={e => handleUserInputChange('confirmPassword', e.target.value)} isInvalid={!!validationErrors.confirmPassword} errorMessage={validationErrors.confirmPassword} endContent={<button className="focus:outline-none" type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                                {showConfirmPassword ? <EyeSlashIcon size={20} className="text-gray-400 pointer-events-none" /> : <EyeIcon size={20} className="text-gray-400 pointer-events-none" />}
                              </button>} classNames={{
                  input: "box-border text-white",
                  inputWrapper: "box-border"
                }} />
                        </>}
                      {isEditing ? <div className="flex flex-col gap-2">
                          <label className="text-sm text-gray-300">Status</label>
                          <div className="flex gap-2">
                            <Button size="sm" color={userFormData.status === 'active' ? 'success' : 'default'} variant={userFormData.status === 'active' ? 'solid' : 'bordered'} onPress={() => handleUserStatusChange(viewingItem, 'active')} isDisabled={userFormData.status === 'active' || changingStatusId !== null} isLoading={changingStatusId === viewingItem?.id} className="cursor-pointer">
                              {userFormData.status === 'active' ? 'Active' : 'Activate'}
                            </Button>
                            <Button size="sm" color={userFormData.status !== 'active' ? 'danger' : 'default'} variant={userFormData.status !== 'active' ? 'solid' : 'bordered'} onPress={() => handleUserStatusChange(viewingItem, 'inactive')} isDisabled={userFormData.status !== 'active' || changingStatusId !== null} isLoading={changingStatusId === viewingItem?.id} className="cursor-pointer">
                              {userFormData.status !== 'active' ? 'Deactivated' : 'Deactivate'}
                            </Button>
                          </div>
                        </div> : <Select aria-label="Status" styles={TzSelectStylesFullWidth} className="react-select-container font-inter z-[9999] pr-2 w-full custom-scrollbar2" placeholder="Select user status" value={userFormData.status ? {
                value: userFormData.status,
                label: ['Active', 'Inactive', 'Pending', 'Suspended'].find(o => o.toLowerCase() === userFormData.status) ?? userFormData.status
              } : null} onChange={(newValue: unknown) => {
                const option = newValue as {
                  value: string;
                  label: string;
                } | null;
                handleUserInputChange('status', option?.value ?? '');
              }} options={['Active', 'Inactive', 'Pending', 'Suspended'].map(option => ({
                value: option.toLowerCase(),
                label: option
              }))} noOptionsMessage={() => "No options available"} />}
                      {isEditing ? <div className="flex flex-col gap-2">
                          <label className="text-sm text-gray-300">Admin Access</label>
                          <div className="flex gap-2">
                            <Button size="sm" color={userFormData.is_admin ? 'secondary' : 'default'} variant={userFormData.is_admin ? 'solid' : 'bordered'} onPress={() => handleAdminStatusChange(viewingItem)} isDisabled={userFormData.is_admin || changingAccessId !== null} isLoading={changingAccessId === viewingItem?.id} className="cursor-pointer">
                              {userFormData.is_admin ? 'Tenant Admin' : 'Assign Tenant Admin'}
                            </Button>
                            <Button size="sm" color={!userFormData.is_admin ? 'default' : 'default'} variant={!userFormData.is_admin ? 'solid' : 'bordered'} onPress={() => handleAdminStatusChange(viewingItem)} isDisabled={!userFormData.is_admin || changingAccessId !== null} isLoading={changingAccessId === viewingItem?.id} className="cursor-pointer">
                              {!userFormData.is_admin ? 'No Admin Access' : 'Revoke Admin'}
                            </Button>
                          </div>
                        </div> : <Checkbox isSelected={userFormData.is_admin} onValueChange={checked => handleUserInputChange('is_admin', checked)} classNames={{
                base: "box-border",
                wrapper: "box-border",
                label: "box-border text-sm text-gray-300"
              }}>
                          Admin privileges
                        </Checkbox>}
                    </div>}
                </ModalBody>
                <ModalFooter>
                  {isViewMode ? <Button color="primary" variant="solid" onPress={onClose} className="cursor-pointer">
                      Close
                    </Button> : <div className="flex flex-row gap-2 justify-between w-full">
                      <div className="flex flex-row gap-2">
                        <Button color="default" variant="flat" onPress={() => {
                  resetUserModalState();
                  onClose();
                }} className="cursor-pointer">
                          Cancel
                        </Button>
                        <Button color="primary" variant="solid" onPress={handleUserSubmit} className="cursor-pointer" isDisabled={saving} isLoading={saving}>
                          {isEditing ? 'Update' : 'Create'}
                        </Button>
                      </div>
                    </div>}
                </ModalFooter>
              </>}
          </ModalContent>
        </Modal>

         {/* Case Modal */}
         <Modal isOpen={isCaseModalOpen} onOpenChange={open => {
      if (!open) resetCaseModalState();
      onCaseModalOpenChange();
    }} size="2xl" classNames={{
      base: "box-border bg-gray-800 border-white/10",
      header: "box-border",
      body: "box-border py-6",
      footer: "box-border px-4",
      closeButton: "text-2xl hover:bg-white/0 hover:text-white cursor-pointer pt-3.5"
    }} data-sentry-element="Modal" data-sentry-source-file="AdminPanel.tsx">
           <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="AdminPanel.tsx">
            {onClose => <>
                 <ModalHeader className="flex bg-white/10 px-6 py-4 uppercase tracking-widest">
                   {isCaseViewMode && viewingItem ? <div className="flex items-center gap-4">
                       <span>{viewingItem.name}</span>
                       <Tooltip content="Edit case" placement="top" delay={600} showArrow={true} classNames={{
                content: 'bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm max-w-[300px] break-words',
                base: 'before:bg-tooltip-bg before:rounded-[2px] before:shadow-none before:drop-shadow-none '
              }}>
                         <Button variant="light" size="sm" onPress={() => {
                  setIsCaseViewMode(false);
                  setIsEditing(true);
                  setCaseFormData({
                    name: viewingItem.name,
                    description: viewingItem.description || '',
                    owner_id: viewingItem.owner_id || ''
                  });
                  setEditingItem(viewingItem);
                }} className="cursor-pointer text-white hover:bg-white/10" aria-label="Edit case" isIconOnly>
                           <PencilIcon size={20} />
                         </Button>
                       </Tooltip>
                     </div> : isEditing ? 'Edit Case' : 'Add Case'}
                 </ModalHeader>
                <ModalBody>
                  {isCaseViewMode && viewingItem ? <div className="space-y-6">
                      {/* Case Details */}
                      <div className="grid grid-cols-2 gap-6">
                        <div className="flex flex-row gap-2 col-span-2">
                          <span className="font-semibold text-white m-0 p-0">Description:</span>
                          <span className="text-gray-300">{viewingItem.description || 'No description'}</span>
                        </div>
                        <div className="flex flex-row gap-2">
                          <span className="font-semibold text-white m-0 p-0">Status:</span>
                          <span className="text-gray-300">{viewingItem.status}</span>
                        </div>

                        <div className="flex flex-row gap-2">
                          <span className="font-semibold text-white m-0 p-0">Created:</span>
                          <span className="text-gray-300">
                            {viewingItem.created_at ? new Date(viewingItem.created_at).toLocaleDateString() : 'Unknown'}
                          </span>
                        </div>
                        <div className="flex flex-row gap-2">
                          <span className="font-semibold text-white m-0 p-0">Updated:</span>
                          <span className="text-gray-300">
                            {viewingItem.updated_at ? new Date(viewingItem.updated_at).toLocaleDateString() : 'Unknown'}
                          </span>
                        </div>
                        <div className="flex flex-row gap-2">
                          <span className="font-semibold text-white m-0 p-0">Users:</span>
                          <span className="text-gray-300">{caseUsers.length} users</span>
                        </div>
                        <div className="flex flex-row gap-2">
                          <span className="font-semibold text-white m-0 p-0 whitespace-nowrap">Case ID:</span>
                          <span className="text-gray-300 text-sm font-mono">{viewingItem.id}</span>
                        </div>
                        <div className="flex flex-row gap-2">
                            <Button color={viewingItem.status === 'active' ? 'default' : 'danger'} variant="flat" onPress={() => handleUpdateCaseStatus(viewingItem)} className="cursor-pointer">
                              {viewingItem?.status === 'active' ? 'Deactivate Case' : 'Reactivate Case'}
                            </Button>
                        </div>
                      </div>

                      {/* Case Users Section */}
                      <div className="border-t border-solid border-white/10 pt-6 mt-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-semibold text-white">Case Users</h3>
                          <div className="flex gap-2">
                            <Button color="primary" onPress={onAddCaseUserModalOpen} startContent={<PlusIcon size={20} />} isDisabled={availableCaseUsers.length === 0} size="sm" className="cursor-pointer">
                              Add User
                            </Button>
                          </div>
                        </div>

                        {caseUsersLoading ? <LoadingCard message="Loading case users..." variant="inline" /> : caseUsersError ? <div className="text-center py-8">
                            <p className="text-red-400 mb-4">Error: {caseUsersError}</p>
                            <Button color="primary" onPress={() => viewingItem && fetchCaseUsers(viewingItem.id)} className="cursor-pointer">
                              Retry
                            </Button>
                          </div> : caseUsers.length === 0 ? <div className="text-center py-8">
                            <UserCircleIcon size={64} className="text-gray-400 mx-auto mb-4" />
                            <h4 className="text-lg font-semibold text-white mb-2">No Users Assigned</h4>
                            <p className="text-gray-300 mb-6">
                              No users have been assigned to this case yet.
                            </p>
                            <Button color="primary" onPress={onAddCaseUserModalOpen} startContent={<PlusIcon size={20} />} className="cursor-pointer">
                              Add First User
                            </Button>
                          </div> : <div className="space-y-4">
                            {/* Users Table */}
                            <Table aria-label="Case users table" classNames={{
                    base: "box-border bg-transparent shadow-none",
                    wrapper: "box-border p-0 m-0 bg-transparent shadow-none overflow-none",
                    table: "bg-transparent shadow-none p-0 m-0 rounded-none w-full",
                    th: "bg-white/25 text-gray-300 tracking-widest hover:bg-white/25",
                    tr: "hover:bg-brand-blue-light/20",
                    td: "border-b-1 border-white/10 border-solid"
                  }}>
                              <TableHeader>
                                <TableColumn>USER</TableColumn>
                                <TableColumn>ROLE</TableColumn>
                                <TableColumn>ASSIGNED</TableColumn>
                                <TableColumn>ACTIONS</TableColumn>
                              </TableHeader>
                              <TableBody>
                                {caseUsers.map(caseUser => <TableRow key={caseUser.user_id}>
                                    <TableCell>
                                      <div className="flex items-center gap-3">
                                        <div className="flex flex-col gap-1">
                                          <span className="font-semibold text-white">{caseUser.user_name || 'Unknown'}</span>
                                          <span className="text-sm text-gray-400 italic">{caseUser.user_email}</span>
                                        </div>
                                      </div>
                                    </TableCell>
                                    <TableCell>
                                     {caseUser.case_role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                    </TableCell>
                                    <TableCell>
                                      <span className="text-sm text-gray-300">
                                        {caseUser.granted_at ? formatDate(caseUser.granted_at) : ""}
                                      </span>
                                    </TableCell>
                                    <TableCell>
                                      <div className="flex items-center gap-2">
                                        <Tooltip content="Remove user from case" placement="top" delay={1000} showArrow={true} classNames={{
                              content: 'bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm max-w-[300px] break-words',
                              base: 'before:bg-tooltip-bg before:rounded-[2px] before:shadow-none before:drop-shadow-none '
                            }}>
                                          <Button isIconOnly size="sm" variant="light" color="danger" onPress={() => handleRemoveCaseUser(caseUser.user_id, caseUser.access_type, caseUser.group_id, caseUser.assignment_id)} aria-label="Remove user" className="hover:bg-white/10 cursor-pointer">
                                            <TrashIcon size={16} />
                                          </Button>
                                        </Tooltip>
                                      </div>
                                    </TableCell>
                                  </TableRow>)}
                              </TableBody>
                            </Table>
                          </div>}
                      </div>
                    </div> : <div className="flex flex-col gap-4">
                      <Input aria-label="Case Name" placeholder="Enter case name" value={caseFormData.name} onChange={e => {
                setCaseFormData({
                  ...caseFormData,
                  name: e.target.value
                });
                if (validationErrors.name) {
                  setValidationErrors({
                    ...validationErrors,
                    name: undefined
                  });
                }
              }} isRequired isInvalid={!!validationErrors.name} errorMessage={validationErrors.name} classNames={{
                input: "box-border text-white",
                inputWrapper: "box-border"
              }} />
                      <Textarea aria-label="Description" placeholder="Enter case description" value={caseFormData.description} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCaseFormData({
                ...caseFormData,
                description: e.target.value
              })} classNames={{
                input: "box-border text-white",
                inputWrapper: "box-border h-fit"
              }} />
                      <Select aria-label="Select case owner" key="case-owner-select" isClearable styles={{
                ...TzSelectStylesFullWidth,
                menuPortal: base => ({
                  ...base,
                  zIndex: 9999,
                  color: 'black'
                })
              }} menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined} className="react-select-container font-inter pr-2 w-full custom-scrollbar2" placeholder={usersLoading ? 'Loading users...' : 'Select a case owner (optional)'} value={caseFormData.owner_id ? (() => {
                const owner = users.find(u => u.id === caseFormData.owner_id);
                return owner ? {
                  value: caseFormData.owner_id,
                  label: `${owner.name} (${owner.email})`
                } : {
                  value: caseFormData.owner_id,
                  label: caseFormData.owner_id
                };
              })() : null} onChange={(newValue: unknown) => {
                const option = newValue as {
                  value: string;
                  label: string;
                } | null;
                setCaseFormData({
                  ...caseFormData,
                  owner_id: option?.value ?? ''
                });
              }} options={activeUsers?.map(user => ({
                value: user.id,
                label: `${user.name} (${user.email})`
              })) ?? []} noOptionsMessage={() => "No users available"} />
                    </div>}
                </ModalBody>
                <ModalFooter>
                  {isCaseViewMode ? <Button color="primary" variant="solid" onPress={onClose} className="cursor-pointer">
                      Close
                    </Button> : <div className="flex flex-row gap-2 justify-between w-full">
                      {isEditing && viewingItem && <Button color={viewingItem.status === 'active' ? 'default' : 'danger'} variant="flat" onPress={() => handleUpdateCaseStatus(viewingItem)} className="cursor-pointer">
                          {viewingItem?.status === 'active' ? 'Deactivate Case' : 'Reactivate Case'}
                        </Button>}
                      <div className="flex flex-row gap-2 ml-auto">
                        <Button color="default" variant="flat" onPress={() => {
                  resetCaseModalState();
                  onClose();
                }} className="cursor-pointer">
                          Cancel
                        </Button>
                        <Button color="primary" variant="solid" onPress={handleCaseSubmit} className="cursor-pointer" isDisabled={saving} isLoading={saving}>
                          {isEditing ? 'Update' : 'Create'}
                        </Button>
                      </div>
                    </div>}
                </ModalFooter>
              </>}
          </ModalContent>
        </Modal>

         {/* Group Modal */}
         <Modal isOpen={isGroupModalOpen} onOpenChange={open => {
      if (!open) {
        resetGroupModalState();
        onGroupModalClose();
      } else {
        onGroupModalOpen();
      }
    }} isDismissable={false} size="2xl" classNames={{
      base: "box-border bg-gray-800 border-white/10 h-[calc(100vh-150px)] overflow-hidden",
      header: "box-border",
      body: "box-border py-6 overflow-y-auto custom-scrollbar2",
      footer: "box-border px-4",
      closeButton: "!hidden"
    }} data-sentry-element="Modal" data-sentry-source-file="AdminPanel.tsx">
           <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="AdminPanel.tsx">
            {onClose => <>
                 <ModalHeader className="flex flex-row items-center justify-between bg-white/10 px-6 py-4 uppercase tracking-widest">
                   <div className="flex items-center gap-4">
                   {isGroupViewMode && viewingItem ? <>
                       <span>{viewingItem.name}</span>
                       <Tooltip content="Edit group" placement="top" delay={600} showArrow={true} classNames={{
                  content: 'bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm max-w-[300px] break-words',
                  base: 'before:bg-tooltip-bg before:rounded-[2px] before:shadow-none before:drop-shadow-none '
                }}>
                         <Button variant="light" size="sm" onPress={() => {
                    setIsGroupViewMode(false);
                    setIsEditing(true);
                    setGroupForm({
                      name: viewingItem.name,
                      description: viewingItem.description || '',
                      is_active: viewingItem.is_active
                    });
                    setEditingItem(viewingItem);
                  }} className="cursor-pointer text-white hover:bg-white/10" aria-label="Edit group" isIconOnly>
                           <PencilIcon size={20} />
                         </Button>
                       </Tooltip>
                     </> : isEditing ? 'Edit Group' : 'Add Group'}
                   </div>
                   <Button isIconOnly variant="light" size="sm" aria-label="Close" onPress={() => {
              onClose();
              resetGroupModalState();
              onGroupModalClose();
            }} className="cursor-pointer text-white hover:bg-white/10 text-2xl min-w-0">
                     ×
                   </Button>
                 </ModalHeader>
                <ModalBody>
                  {isGroupViewMode && viewingItem ? <div className="p-0 m-0">
                      {/* Group Details */}
                      <div className="grid grid-cols-2 gap-6">
                        <div className="flex flex-row gap-2 col-span-2">
                          <span className="font-semibold text-white m-0 p-0">Description:</span>
                          <span className="text-gray-300">{viewingItem.description || 'No description'}</span>
                        </div>
                        <div className="flex flex-row gap-2">
                          <span className="font-semibold text-white m-0 p-0">Status:</span>
                          <span className="text-gray-300">{viewingItem.is_active ? 'Active' : 'Inactive'}</span>
                        </div>
                        <div className="flex flex-row gap-2">
                          <span className="font-semibold text-white m-0 p-0">Members:</span>
                          <span className="text-gray-300">{groupMembers.length} members</span>
                        </div>
                        <div className="flex flex-row gap-2">
                          <span className="font-semibold text-white m-0 p-0">Cases:</span>
                          <span className="text-gray-300">{groupCases.length} cases</span>
                        </div>
                        <div className="flex flex-row gap-2">
                          <span className="font-semibold text-white m-0 p-0">Created:</span>
                          <span className="text-gray-300">
                            {viewingItem.created_at ? new Date(viewingItem.created_at).toLocaleDateString() : 'Unknown'}
                          </span>
                        </div>
                        <div className="flex flex-row gap-2">
                          <span className="font-semibold text-white m-0 p-0">Updated:</span>
                          <span className="text-gray-300">
                            {viewingItem.updated_at ? new Date(viewingItem.updated_at).toLocaleDateString() : 'Unknown'}
                          </span>
                        </div>
                        <div className="flex flex-row gap-2">
                          <span className="font-semibold text-white m-0 p-0 whitespace-nowrap">Group ID:</span>
                          <span className="text-gray-300 text-sm font-mono">{viewingItem.id}</span>
                        </div>
                      </div>
                      <Divider className="mt-4 border-solid border-white/50 border-1" orientation="horizontal" />
                      <Tabs aria-label="Group members and cases" color="secondary" variant="underlined" size="lg" radius="full" classNames={{
                base: "box-border"
              }}>
                        <Tab key="members" title="Members">
                          <div className="">
                            {groupMembersLoading ? <LoadingCard message="Loading group members..." variant="inline" /> : groupMembersError ? <div className="text-center py-8">
                                <p className="text-red-400 mb-4">Error: {groupMembersError}</p>
                                <Button color="primary" onPress={() => viewingItem && fetchGroupMembers(viewingItem.id)} className="cursor-pointer">
                                  Retry
                                </Button>
                              </div> : groupMembers.length === 0 ? <div className="text-center py-8">
                                <UserIcon size={64} className="text-gray-400 mx-auto mb-4" />
                                <h4 className="text-lg font-semibold text-white mb-2">No Members Found</h4>
                                <p className="text-gray-300 mb-6">
                                  This group has no members yet. Add your first member to get started.
                                </p>
                                <Button color="primary" onPress={onGroupMemberModalOpen} startContent={<PlusIcon size={20} />} className="cursor-pointer">
                                  Add First Member
                                </Button>
                              </div> : <div className="space-y-4">
                                {/* Members Table */}
                                <Table aria-label="Group members table" classNames={{
                        base: "box-border bg-transparent shadow-none max-h-[500px]",
                        wrapper: "box-border p-0 m-0 bg-transparent shadow-none overflow-none",
                        table: "bg-transparent shadow-none p-0 m-0 rounded-none w-full",
                        th: "bg-white/25 text-gray-300 tracking-widest hover:bg-white/25",
                        tr: "hover:bg-brand-blue-light/20",
                        td: "border-b-1 border-white/10 border-solid"
                      }}>
                                  <TableHeader>
                                    <TableColumn>MEMBER</TableColumn>
                                    <TableColumn>STATUS</TableColumn>
                                    <TableColumn>JOINED</TableColumn>
                                    <TableColumn>ACTIONS</TableColumn>
                                  </TableHeader>
                                  <TableBody>
                                    {groupMembers.map(member => <TableRow key={member.id}>
                                        <TableCell>
                                          <div className="flex items-center gap-3">
                                            <UserIcon size={20} className="text-gray-400" />
                                            <div>
                                              <p className="font-semibold text-white">{member.user.name}</p>
                                              <p className="text-sm text-gray-300">{member.user.email}</p>
                                            </div>
                                          </div>
                                        </TableCell>
                                        <TableCell>
                                          <span className="text-sm text-gray-300">
                                            {member.is_active ? 'Active' : 'Inactive'}
                                          </span>
                                        </TableCell>
                                        <TableCell>
                                          <span className="text-sm text-gray-300">
                                            {member.joined_at ? formatDate(member.joined_at) : ""}
                                          </span>
                                        </TableCell>
                                        <TableCell>
                                          <div className="flex items-center gap-2">
                                            <Tooltip content={member?.is_active ? 'Deactivate member' : 'Reactivate member'} placement="top" delay={1000} showArrow={true} classNames={{
                                  content: 'bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm max-w-[300px] break-words',
                                  base: 'before:bg-tooltip-bg before:rounded-[2px] before:shadow-none before:drop-shadow-none '
                                }}>
                                              <Button isIconOnly size="sm" variant="light" color={member.is_active ? 'danger' : 'success'} onPress={() => handleMemberStatusChange(member)} aria-label={member.is_active ? 'Deactivate member' : 'Reactivate member'} isLoading={changingStatusId === member.id} className="hover:bg-white/10 cursor-pointer">
                                                {changingStatusId !== member.id && (member.is_active ? <ProhibitIcon size={20} /> : <CheckCircleIcon size={20} />)}
                                              </Button>
                                            </Tooltip>
                                          </div>
                                        </TableCell>
                                      </TableRow>)}
                                  </TableBody>
                                </Table>
                                {viewingItem.is_active && <div className="flex items-center justify-between mb-4">
                                  <Button color="primary" onPress={onGroupMemberModalOpen} startContent={<PlusIcon size={20} />} isDisabled={availableUsers.length === 0} size="sm" className="cursor-pointer">
                                    Add Member
                                  </Button>
                                </div>}
                              </div>}
                          </div>
                        </Tab>
                        {enableCaseManagement && <Tab key="cases" title="Cases">
                          <div className="">
                            {groupCasesLoading ? <LoadingCard message="Loading group cases..." variant="inline" /> : groupCasesError ? <div className="text-center py-8">
                              <p className="text-red-400 mb-4">Error: {groupCasesError}</p>
                              <Button color="primary" onPress={() => viewingItem && fetchGroupCases(viewingItem.id)} className="cursor-pointer">
                                Retry
                              </Button>
                            </div> : groupCases.length === 0 ? <div className="text-center py-8">
                              <BriefcaseMetalIcon size={64} className="text-gray-400 mx-auto mb-4" />
                              <h4 className="text-lg font-semibold text-white mb-2">No Cases Assigned</h4>
                              <p className="text-gray-300 mb-6">
                                This group has no cases assigned yet.
                              </p>
                              {viewingItem.is_active && <Button color="primary" onPress={onGroupCaseModalOpen} startContent={<PlusIcon size={20} />} isDisabled={availableCases.length === 0} size="sm" className="cursor-pointer">
                                  Add First Case
                                </Button>}
                            </div> : <div className="space-y-4">
                              {/* Cases Table */}
                              <Table aria-label="Group cases table" classNames={{
                        base: "box-border bg-transparent shadow-none max-h-[500px]",
                        wrapper: "box-border p-0 m-0 bg-transparent shadow-none overflow-none",
                        table: "bg-transparent shadow-none p-0 m-0 rounded-none w-full",
                        th: "bg-white/25 text-gray-300 tracking-widest hover:bg-white/25",
                        tr: "hover:bg-brand-blue-light/20",
                        td: "border-b-1 border-white/10 border-solid"
                      }}>
                                <TableHeader>
                                  <TableColumn>CASE</TableColumn>
                                  <TableColumn>STATUS</TableColumn>
                                  <TableColumn>ROLE</TableColumn>
                                  <TableColumn>ASSIGNED</TableColumn>
                                  <TableColumn>ACTIONS</TableColumn>
                                </TableHeader>
                                <TableBody>
                                  {groupCases.map(assignment => <TableRow key={assignment.id}>
                                      <TableCell>
                                        <div className="flex items-center gap-3">
                                          <div>
                                            <p className="font-semibold text-white">{assignment.case.name}</p>
                                            <p className="text-sm text-gray-300">{assignment.case.description || 'No description'}</p>
                                          </div>
                                        </div>
                                      </TableCell>
                                      <TableCell>
                                        <span className="text-sm text-gray-300">
                                          {assignment.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                      </TableCell>
                                      <TableCell>
                                        <span className="text-sm text-gray-300">
                                          {getRoleName(assignment.case_role)}
                                        </span>
                                      </TableCell>
                                      <TableCell>
                                        <span className="text-sm text-gray-300">
                                          {assignment.assigned_at ? formatDate(assignment.assigned_at) : ""}
                                        </span>
                                      </TableCell>
                                      <TableCell>
                                        <div className="flex items-center gap-2">
                                          <Tooltip content={assignment.is_active ? 'Deactivate case access' : 'Reactivate case access'} placement="top" delay={1000} showArrow={true} classNames={{
                                  content: 'bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm max-w-[300px] break-words',
                                  base: 'before:bg-tooltip-bg before:rounded-[2px] before:shadow-none before:drop-shadow-none '
                                }}>
                                            <Button isIconOnly size="sm" variant="light" color={assignment.is_active ? 'danger' : 'success'} onPress={() => handleCaseStatusChange(assignment)} aria-label={assignment.is_active ? 'Deactivate case access' : 'Reactivate case access'} isLoading={changingCaseStatusId === assignment.id} className="hover:bg-white/10 cursor-pointer">
                                              {changingCaseStatusId !== assignment.id && (assignment.is_active ? <ProhibitIcon size={20} /> : <CheckCircleIcon size={20} />)}
                                            </Button>
                                          </Tooltip>
                                        </div>
                                      </TableCell>
                                    </TableRow>)}
                                </TableBody>
                              </Table>
                              {viewingItem.is_active && <div className="flex items-center justify-between mb-4">
                                  <Button color="primary" onPress={onGroupCaseModalOpen} startContent={<PlusIcon size={20} />} isDisabled={availableCases.length === 0} size="sm" className="cursor-pointer">
                                    Add Case
                                  </Button>
                                </div>}
                            </div>}
                        </div>
                          </Tab>}
                      </Tabs>
                    </div> : <div className="flex flex-col gap-4">
                      <Input label="Group Name" placeholder="Enter group name" value={groupForm.name} onChange={e => handleGroupInputChange('name', e.target.value)} isInvalid={!!validationErrors.name} errorMessage={validationErrors.name} isRequired aria-required="true" classNames={{
                input: "box-border text-white",
                inputWrapper: "box-border"
              }} />
                      <Textarea label="Description" placeholder="Enter group description" value={groupForm.description} onChange={e => handleGroupInputChange('description', e.target.value)} classNames={{
                input: "box-border text-white",
                inputWrapper: "box-border h-fit",
                label: "box-border text-sm text-gray-300"
              }} />
                    </div>}
                </ModalBody>
                <ModalFooter>
                  {isGroupViewMode ? <div className="flex flex-row gap-2 justify-between w-full">
                      {viewingItem && (viewingItem?.is_active ? <Button color="danger" variant="flat" onPress={() => handleGroupStatusChange(viewingItem)} isLoading={changingStatusId === viewingItem.id} className="cursor-pointer" startContent={changingStatusId !== viewingItem.id ? <ProhibitIcon size={20} /> : undefined}>
                            {changingStatusId !== viewingItem.id ? 'Deactivate Group' : 'Deactivating...'}
                          </Button> : <Button color="success" variant="flat" onPress={() => handleGroupStatusChange(viewingItem)} isLoading={changingStatusId === viewingItem.id} className="cursor-pointer" startContent={changingStatusId !== viewingItem.id ? <CheckCircleIcon size={20} /> : undefined}>
                            {changingStatusId !== viewingItem.id ? 'Reactivate Group' : 'Reactivating...'}
                          </Button>)}
                      <Button color="primary" variant="solid" onPress={() => {
                onClose();
                resetGroupModalState();
                onGroupModalClose();
              }} className="cursor-pointer">
                        Close
                      </Button>
                    </div> : <div className="flex flex-row gap-2 justify-between w-full">
                      {isEditing && viewingItem && (viewingItem.is_active ? <Button color="danger" variant="flat" onPress={() => handleGroupStatusChange(viewingItem)} isLoading={changingStatusId === viewingItem.id} className="cursor-pointer" startContent={changingStatusId !== viewingItem.id ? <ProhibitIcon size={20} /> : undefined}>
                            {changingStatusId !== viewingItem.id ? 'Deactivate Group' : 'Deactivating...'}
                          </Button> : <Button color="success" variant="flat" onPress={() => handleGroupStatusChange(viewingItem)} isLoading={changingStatusId === viewingItem.id} className="cursor-pointer" startContent={changingStatusId !== viewingItem.id ? <CheckCircleIcon size={20} /> : undefined}>
                            {changingStatusId !== viewingItem.id ? 'Reactivate Group' : 'Reactivating...'}
                          </Button>)}
                      <div className="flex flex-row gap-2">
                        <Button color="default" variant="flat" onPress={() => {
                  onClose();
                  resetGroupModalState();
                  onGroupModalClose();
                }} className="cursor-pointer">
                          Cancel
                        </Button>
                        <Button color="primary" variant="solid" onPress={handleGroupSubmit} className="cursor-pointer" isDisabled={saving} isLoading={saving}>
                          {isEditing ? 'Update' : 'Create'}
                        </Button>
                      </div>
                    </div>}
                </ModalFooter>
              </>}
          </ModalContent>
         </Modal>


         {/* Add Group Member Modal */}
         <Modal isOpen={isGroupMemberOpen} onOpenChange={onGroupMemberModalOpenChange} size="md" classNames={{
      base: "box-border bg-gray-800 border-white/10",
      header: "box-border",
      body: "box-border py-6",
      footer: "box-border",
      closeButton: "text-2xl hover:bg-white/0 hover:text-white cursor-pointer pt-3"
    }} data-sentry-element="Modal" data-sentry-source-file="AdminPanel.tsx">
           <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="AdminPanel.tsx">
             {onClose => <>
                 <ModalHeader className="flex bg-white/10 px-6 py-4 uppercase tracking-widest">
                   Add Member to Group
                 </ModalHeader>
                 <ModalBody>
                   <Select aria-label="Select User" styles={{
              ...TzSelectStylesFullWidth,
              menu: base => ({
                ...base,
                zIndex: 9999,
                color: 'black'
              })
            }} className="react-select-container font-inter z-[9999] pr-2 w-full custom-scrollbar2" placeholder="Choose a user to add" value={selectedUserId ? (() => {
              const user = availableUsers.find(u => u.id === selectedUserId);
              return user ? {
                value: selectedUserId,
                label: `${user.name} (${user.email})`
              } : {
                value: selectedUserId,
                label: selectedUserId
              };
            })() : null} onChange={(newValue: unknown) => {
              const option = newValue as {
                value: string;
                label: string;
              } | null;
              setSelectedUserId(option?.value ?? '');
            }} options={availableUsers.map(user => ({
              value: user.id,
              label: `${user.name} (${user.email})`
            }))} noOptionsMessage={() => "No users available"} />
                 </ModalBody>
                 <ModalFooter>
                   <Button color="danger" variant="flat" onPress={() => {
              setSelectedUserId('');
              onClose();
            }} className="cursor-pointer">
                     Cancel
                   </Button>
                   <Button color="primary" onPress={async () => {
              try {
                await handleAddMember();
                onClose();
              } catch {
                // Error shown via groupMembersError
              }
            }} isLoading={assigningUser} isDisabled={!selectedUserId} className="cursor-pointer">
                     Add Member
                   </Button>
                 </ModalFooter>
               </>}
           </ModalContent>
         </Modal>

         {/* Add Group Case Modal */}
         <Modal isOpen={isGroupCaseOpen} onOpenChange={onGroupCaseModalOpenChange} isDismissable={false} size="md" classNames={{
      base: "box-border bg-gray-800 border-white/10",
      header: "box-border",
      body: "box-border py-6",
      footer: "box-border",
      closeButton: "text-2xl hover:bg-white/0 hover:text-white cursor-pointer pt-3"
    }} data-sentry-element="Modal" data-sentry-source-file="AdminPanel.tsx">
           <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="AdminPanel.tsx">
             {onClose => <>
                 <ModalHeader className="flex bg-white/10 px-6 py-4 uppercase tracking-widest">
                   Add Case to Group
                 </ModalHeader>
                 <ModalBody>
                   <div className="space-y-4">
                     <Select aria-label="Select Case" styles={{
                ...TzSelectStylesFullWidth,
                menuPortal: base => ({
                  ...base,
                  zIndex: 9999,
                  color: 'black'
                })
              }} menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined} className="react-select-container font-inter pr-2 w-full custom-scrollbar2" placeholder="Choose a case to add" value={selectedCaseId ? (() => {
                const caseItem = availableCases.find(c => c.id === selectedCaseId);
                return caseItem ? {
                  value: selectedCaseId,
                  label: `${caseItem.name} (${caseItem.case_number})`
                } : {
                  value: selectedCaseId,
                  label: selectedCaseId
                };
              })() : null} onChange={(newValue: unknown) => {
                const option = newValue as {
                  value: string;
                  label: string;
                } | null;
                setSelectedCaseId(option?.value ?? '');
              }} options={availableCases.map(caseItem => ({
                value: caseItem.id,
                label: `${caseItem.name} (${caseItem.case_number})`
              }))} noOptionsMessage={() => "No cases available"} />
                     <Select aria-label="Role" styles={{
                ...TzSelectStylesFullWidth,
                menuPortal: base => ({
                  ...base,
                  zIndex: 9999,
                  color: 'black'
                })
              }} menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined} className="react-select-container font-inter pr-2 w-full custom-scrollbar2" placeholder="Select case role" value={selectedRole ? {
                value: selectedRole,
                label: ROLE_OPTIONS.find(r => r.key === selectedRole)?.label ?? selectedRole
              } : null} onChange={(newValue: unknown) => {
                const option = newValue as {
                  value: string;
                  label: string;
                } | null;
                setSelectedRole(option?.value ?? 'member');
              }} options={ROLE_OPTIONS.map(role => ({
                value: role.key,
                label: role.label
              }))} noOptionsMessage={() => "No roles available"} />
                   </div>
                 </ModalBody>
                 <ModalFooter>
                   <Button color="danger" variant="flat" onPress={() => {
              setSelectedCaseId('');
              setSelectedRole('member');
              onClose();
            }} className="cursor-pointer">
                     Cancel
                   </Button>
                   <Button color="primary" onPress={async () => {
              try {
                await handleAddCase();
                onClose();
              } catch {
                // Error shown via groupCasesError
              }
            }} isLoading={assigningCase} isDisabled={!selectedCaseId || !selectedRole} className="cursor-pointer">
                     Add Case
                   </Button>
                 </ModalFooter>
               </>}
           </ModalContent>
         </Modal>

         {/* Add Case User Modal */}
         <Modal isOpen={isAddCaseUserModalOpen} onOpenChange={onAddCaseUserModalOpenChange} isDismissable={false} size="2xl" classNames={{
      base: "box-border bg-gray-800 border-white/10",
      header: "box-border",
      body: "box-border py-6",
      footer: "box-border",
      closeButton: "text-2xl hover:bg-white/0 hover:text-white cursor-pointer pt-3"
    }} data-sentry-element="Modal" data-sentry-source-file="AdminPanel.tsx">
           <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="AdminPanel.tsx">
             {onClose => <>
                 <ModalHeader className="flex bg-white/10 px-6 py-4 uppercase tracking-widest">
                   Add User to Case
                 </ModalHeader>
                 <ModalBody>
                   <div className="space-y-4">
                     <Select aria-label="Select User" key="user-select" placeholder="Choose a user to add" styles={{
                ...TzSelectStylesFullWidth,
                menuPortal: base => ({
                  ...base,
                  zIndex: 9999,
                  color: 'black'
                })
              }} menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined} className="react-select-container font-inter pr-2 w-full custom-scrollbar2" value={selectedCaseUserId ? (() => {
                const user = availableCaseUsers.find(u => u.id === selectedCaseUserId);
                return user ? {
                  value: selectedCaseUserId,
                  label: `${user.name} (${user.email})`
                } : {
                  value: selectedCaseUserId,
                  label: selectedCaseUserId
                };
              })() : null} onChange={(newValue: unknown) => {
                const option = newValue as {
                  value: string;
                  label: string;
                } | null;
                setSelectedCaseUserId(option?.value ?? '');
              }} options={availableCaseUsers.map(user => ({
                value: user.id,
                label: `${user.name} (${user.email})`
              }))} noOptionsMessage={() => "No users available"} />

                     <Select aria-label="Role" styles={{
                ...TzSelectStylesFullWidth,
                menuPortal: base => ({
                  ...base,
                  zIndex: 9999,
                  color: 'black'
                })
              }} menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined} className="react-select-container font-inter pr-2 w-full custom-scrollbar2" placeholder="Select a role" value={selectedCaseUserRole ? {
                value: selectedCaseUserRole,
                label: {
                  case_owner: 'Case Owner',
                  member: 'Member',
                  viewer: 'Viewer'
                }[selectedCaseUserRole] ?? selectedCaseUserRole
              } : null} onChange={(newValue: unknown) => {
                const option = newValue as {
                  value: string;
                  label: string;
                } | null;
                setSelectedCaseUserRole(option?.value ?? '');
              }} options={[{
                value: 'case_owner',
                label: 'Case Owner'
              }, {
                value: 'member',
                label: 'Member'
              }, {
                value: 'viewer',
                label: 'Viewer'
              }]} noOptionsMessage={() => "No roles available"} />
                   </div>
                 </ModalBody>
                 <ModalFooter>
                   <Button color="danger" variant="light" onPress={onClose} isDisabled={assigningCaseUser} className="cursor-pointer">
                     Cancel
                   </Button>
                   <Button color="primary" onPress={handleAddCaseUser} isLoading={assigningCaseUser} isDisabled={!selectedCaseUserId} className="cursor-pointer">
                     Assign User
                   </Button>
                 </ModalFooter>
               </>}
           </ModalContent>
         </Modal>

         {/* Tenant Admin Modal */}
         <Modal isOpen={isTenantAdminModalOpen} onOpenChange={open => {
      if (open) setTenantAdminError(null);
      onTenantAdminModalOpenChange();
    }} size="2xl" classNames={{
      base: "box-border bg-gray-800 border-white/10",
      header: "box-border",
      body: "box-border py-6",
      footer: "box-border",
      closeButton: "text-2xl hover:bg-white/0 hover:text-white cursor-pointer pt-3"
    }} data-sentry-element="Modal" data-sentry-source-file="AdminPanel.tsx">
           <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="AdminPanel.tsx">
             {onClose => <>
                 <ModalHeader className="flex bg-white/10 px-6 py-4 uppercase tracking-widest">
                   Assign Tenant Admin Role
                 </ModalHeader>
                 <ModalBody>
                   <div className="space-y-4">
                     {tenantAdminError && <p className="text-sm text-red-400" role="alert">{tenantAdminError}</p>}
                     <p className="text-sm text-gray-300">
                       Tenant admins have full access to all cases and users within the tenant.
                     </p>
                     <Select aria-label="Select User" styles={{
                ...MediaSelectStyles,
                menuPortal: base => ({
                  ...base,
                  zIndex: 9999,
                  color: 'black'
                })
              }} menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined} className="react-select-container font-inter pr-2 w-full custom-scrollbar2" placeholder="Choose a user" value={selectedUserForAdmin ? (() => {
                const user = users.find(u => u.id === selectedUserForAdmin);
                if (!user) return {
                  value: selectedUserForAdmin,
                  label: selectedUserForAdmin
                };
                return {
                  value: selectedUserForAdmin,
                  label: `${user.name} (${user.email})${user.is_admin ? ' - Already Admin' : ''}`
                };
              })() : null} onChange={(newValue: unknown) => {
                const option = newValue as {
                  value: string;
                  label: string;
                } | null;
                setSelectedUserForAdmin(option?.value ?? '');
              }} options={users.map(user => ({
                value: user.id,
                label: `${user.name} (${user.email})${user.is_admin ? ' - Already Admin' : ''}`
              }))} noOptionsMessage={() => "No users available"} />
                   </div>
                 </ModalBody>
                 <ModalFooter>
                   <Button color="danger" variant="light" onPress={() => {
              setSelectedUserForAdmin('');
              onClose();
            }} isDisabled={assigningAdmin} className="cursor-pointer">
                     Cancel
                   </Button>
                   <Button color="secondary" onPress={handleAssignTenantAdmin} isLoading={assigningAdmin} isDisabled={!selectedUserForAdmin} className="cursor-pointer">
                     Assign Tenant Admin
                   </Button>
                 </ModalFooter>
               </>}
           </ModalContent>
         </Modal>
       </div>;
};